import { Quest } from 'types/quests'
import { Answer, AnswerOrDraft, Embed } from 'types/answers'
import { User } from 'types/users'
import type { UrlModel } from 'types/urls'
import queryClient from 'api/queryClient'
import { hashKey, type QueryKey } from '@tanstack/react-query'

// Entity reads (getCachedAnswer/Quest/User and the cacheX merges) run thousands of
// times per render pass. peekQueryData builds full query options for
// every call, walking each registered query default, only to hash the key. The app
// sets no queryKeyHashFn, so the default hash is the cache key: look it up directly.
function peekQueryData<T = unknown>(queryKey: QueryKey): T | undefined {
    return queryClient.getQueryCache().get(hashKey(queryKey))?.state.data as T | undefined
}
import { normalizeQuestAncestors } from 'state/questAncestors'
import * as Sentry from '@sentry/react'
import {
    naturalFeedScrollIsActive,
    subscribeNaturalFeedScrollActivity,
} from 'lib/naturalFeedActivity'
import {
    getPendingAnswerOutboxAnswersForQuest,
    removePendingAnswerOutboxConfirmedByQuest,
} from 'state/pendingAnswerOutbox'
import { answerEmbedsHavePermalinkTargets } from 'utils/shareUtils'
import { rememberAnswerHeightFingerprint } from 'lib/answerHeightFingerprint'
import {
    mergeActionRequestsPreservingPrivateMetadata,
    mergeAiRunsPreservingPrivatePayloads,
    shouldPreservePrivateAiActionMetadata,
} from 'lib/aiActionPrivateMetadata'
import {
    adoptAnswerGeometryProjection,
    adoptEmbeddedAnswerGeometryProjection,
    primeAnswerGeometryProjection,
    resolveEmbeddedAnswerGeometryProjection,
} from 'lib/answerGeometryProjection'
import { getSubscribedEntityIds, isCachedEntitySubscribed } from 'state/entityCacheSubscriptions'

/**
 * User record augmented with a cache freshness timestamp.
 * Uses an index signature because the API returns many optional user fields
 * (bio, username, followers_count, etc.) beyond what the core User interface declares.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CachedUser = User & { __cache_ts?: number; [key: string]: any }

/** Shape of a single page in a paginated quests infinite query. */
type QuestEntityRef = { id?: string | number | null }

interface QuestsPage {
    quests: Array<QuestEntityRef & { [key: string]: unknown }>
    count?: number
    has_more?: boolean
    pinnedQuest?: QuestEntityRef | null
    pinned_quest?: QuestEntityRef | null
}

/** Shape of the paginated data stored by react-query for quests streams. */
interface PaginatedQuestsData {
    pages: QuestsPage[]
    pageParams?: unknown[]
}

interface ActionsPage {
    actions?: Array<{ quest?: { id?: string | number | null } | null }>
}

interface PaginatedActionsData {
    pages?: ActionsPage[]
}

type QuestWithAncestors = Quest & {
    grand_parent_id?: string | null
    grand_parent?: Answer | null
}

type CacheQuestOptions = {
    compactThreadAnswers?: boolean
    skipParent?: boolean
    source?: string
    preserveAnswerIds?: Array<string | number>
    preserveViewContextFields?: boolean
    skipStreamQueryTouch?: boolean
}

const compactThreadQuestRefCounts = new Map<string, number>()

const compactThreadAnswerStub = (answer: Answer | null | undefined, index: number) => ({
    id: String(answer?.id || ''),
    quest_id: answer?.quest_id,
    position: Number.isFinite(Number(answer?.position)) ? Number(answer?.position) : index,
    archive: answer?.archive,
    action_tags: answer?.action_tags,
    vote_total: answer?.vote_total,
    crypto_vote_total: answer?.crypto_vote_total,
    bsv_upvalue_amt: answer?.bsv_upvalue_amt,
    user_id: answer?.user_id || answer?.user?.id,
    user: answer?.user?.id == null ? undefined : { id: answer.user.id },
    // quote_main reply-pill counts are derived from the room projection.
    // Preserve the lightweight edge so counts remain complete even when the
    // normalized answer entity has been pruned or has not hydrated yet.
    reply_to_answer_id: answer?.reply_to_answer_id,
    child_quests: answer?.child_quests
        ?.filter(childQuest => childQuest?.id != null)
        .map(childQuest => ({ id: childQuest.id })),
    created_at: answer?.created_at || new Date(0),
    updated_at: answer?.updated_at,
    _thread_transport_stub: true,
})

function compactThreadQuestTransport(quest: QuestWithAncestors): QuestWithAncestors {
    const answersById = new Map(
        (Array.isArray(quest.sorted_answers) ? quest.sorted_answers : [])
            .filter(answer => answer?.id != null)
            .map(answer => [String(answer.id), answer]),
    )
    const answerIds = Array.isArray(quest.sorted_answer_ids)
        ? quest.sorted_answer_ids.map(String)
        : Array.from(answersById.keys())

    return {
        ...quest,
        __knovCompactThreadAnswers: true,
        sorted_answer_ids: answerIds,
        sorted_answers: answerIds.map((answerId, index) => {
            const answer = answersById.get(answerId)
            if (answer?.isLocal || (answer as any)?.isPendingOutbox) return answer
            return compactThreadAnswerStub(answer || ({ id: answerId } as Answer), index)
        }),
    } as QuestWithAncestors
}

function clearCompactThreadQuestTransportMarker(quest: QuestWithAncestors) {
    if (!(quest as any).__knovCompactThreadAnswers) return quest
    const nextQuest = { ...quest } as QuestWithAncestors & {
        __knovCompactThreadAnswers?: boolean
    }
    delete nextQuest.__knovCompactThreadAnswers
    return nextQuest
}

const userNormKey = (id: string | number) => ['userNorm', String(id)]
const QUEST_CACHE_ANOMALY_TTL_MS = 5 * 60 * 1000
const questCacheAnomalySeenAt = new Map<string, number>()
export const ENTITY_CACHE_LIMITS = {
    answer: 2000,
    quest: 600,
    userNorm: 1000,
} as const

type EntityCacheKind = keyof typeof ENTITY_CACHE_LIMITS
type EntityCacheIdSets = Record<EntityCacheKind, Set<string>>
type NormalizedReadCacheKey = `answer:${string}` | `quest:${string}`
type NormalizedReadCacheEntry<T> = {
    source: T
    value: T
    deps: EntityCacheIdSets
}

const normalizedAnswerReadCache = new Map<string, NormalizedReadCacheEntry<Answer>>()
const normalizedQuestReadCache = new Map<string, NormalizedReadCacheEntry<QuestWithAncestors>>()
const normalizedReadDependents: Record<
    EntityCacheKind,
    Map<string, Set<NormalizedReadCacheKey>>
> = {
    answer: new Map(),
    quest: new Map(),
    userNorm: new Map(),
}

const ENTITY_CACHE_PRUNE_INTERVAL_WRITES = 50
const ENTITY_CACHE_PRUNE_MAX_CANDIDATES_PER_SLICE = 25
const ENTITY_CACHE_PRUNE_MIN_IDLE_TIME_MS = 3
const ENTITY_CACHE_PRUNE_FALLBACK_DELAY_MS = 250
const LOCAL_DRAFT_CACHE_GRACE_MS = 10_000
const entityCacheTouchedAt = {
    answer: new Map<string, number>(),
    quest: new Map<string, number>(),
    userNorm: new Map<string, number>(),
}
const entityCacheRetainedUntil = {
    answer: new Map<string, number>(),
    quest: new Map<string, number>(),
    userNorm: new Map<string, number>(),
}
let entityCacheWriteCount = 0
let entityCachePruneRevision = 0
let entityCachePruneHandle: number | null = null
let entityCachePruneHandleKind: 'idle' | 'timeout' | null = null
let entityCachePrunePlan: EntityCachePrunePlan | null = null
let entityCachePruneScrollUnsubscribe: (() => void) | null = null

type EntityCachePruneCandidate = {
    kind: EntityCacheKind
    id: string
    queryHash: string
}

type EntityCachePrunePlan = {
    candidates: EntityCachePruneCandidate[]
    cursor: number
    normalizedReadCachesCleared: boolean
    revision: number
}

type EntityCacheIdleDeadline = {
    timeRemaining: () => number
}

type EntityCacheIdleWindow = Window & {
    requestIdleCallback?: (callback: (deadline: EntityCacheIdleDeadline) => void) => number
    cancelIdleCallback?: (handle: number) => void
}

queryClient.getQueryCache().subscribe(event => {
    if (event.type !== 'added' && event.type !== 'removed' && event.type !== 'updated') return
    const rootKey = event.query?.queryKey?.[0]
    if (rootKey === 'quests' || rootKey === 'actions') entityCachePruneRevision += 1
})

function clearNormalizedReadCaches() {
    normalizedAnswerReadCache.clear()
    normalizedQuestReadCache.clear()
    ;(Object.keys(normalizedReadDependents) as EntityCacheKind[]).forEach(kind => {
        normalizedReadDependents[kind].clear()
    })
}

type EntityCacheSnapshot = {
    existingIds: EntityCacheIdSets
    observedIds: EntityCacheIdSets
    queryHashes: Record<EntityCacheKind, Map<string, string>>
}

function entityQueryKey(kind: EntityCacheKind, id: string) {
    return [kind, id]
}

function normalizedEntityId(value: unknown): string | null {
    if (value === null || value === undefined) return null
    const id = String(value)
    return id ? id : null
}

function getEntityCacheKind(queryKey: unknown): EntityCacheKind | null {
    if (!Array.isArray(queryKey) || queryKey.length !== 2) return null
    const kind = queryKey[0]
    return kind === 'answer' || kind === 'quest' || kind === 'userNorm' ? kind : null
}

function touchEntityCache(kind: EntityCacheKind, id: unknown) {
    const normalizedId = normalizedEntityId(id)
    if (!normalizedId) return

    const touchedAt = entityCacheTouchedAt[kind]
    touchedAt.delete(normalizedId)
    touchedAt.set(normalizedId, Date.now())
}

function createEntityCacheIdSets(): EntityCacheIdSets {
    return {
        answer: new Set<string>(),
        quest: new Set<string>(),
        userNorm: new Set<string>(),
    }
}

function cloneEntityCacheIdSets(deps: EntityCacheIdSets): EntityCacheIdSets {
    return {
        answer: new Set(deps.answer),
        quest: new Set(deps.quest),
        userNorm: new Set(deps.userNorm),
    }
}

function addNormalizedReadDependency(
    deps: EntityCacheIdSets,
    kind: EntityCacheKind,
    idValue: unknown,
) {
    const id = normalizedEntityId(idValue)
    if (!id) return
    deps[kind].add(id)
}

function makeNormalizedReadCacheKey(
    kind: 'answer' | 'quest',
    idValue: unknown,
): NormalizedReadCacheKey | null {
    const id = normalizedEntityId(idValue)
    if (!id) return null
    return `${kind}:${id}` as NormalizedReadCacheKey
}

function readCacheKindAndId(readKey: NormalizedReadCacheKey): {
    kind: 'answer' | 'quest'
    id: string
} {
    if (readKey.startsWith('answer:')) {
        return { kind: 'answer', id: readKey.slice('answer:'.length) }
    }
    return { kind: 'quest', id: readKey.slice('quest:'.length) }
}

function getNormalizedReadCacheEntry(
    readKey: NormalizedReadCacheKey,
): NormalizedReadCacheEntry<Answer | QuestWithAncestors> | undefined {
    const { kind, id } = readCacheKindAndId(readKey)
    return kind === 'answer' ? normalizedAnswerReadCache.get(id) : normalizedQuestReadCache.get(id)
}

function deleteNormalizedReadCacheEntry(readKey: NormalizedReadCacheKey) {
    const { kind, id } = readCacheKindAndId(readKey)
    const entry = getNormalizedReadCacheEntry(readKey)

    if (entry) {
        ;(Object.keys(entry.deps) as EntityCacheKind[]).forEach(depKind => {
            entry.deps[depKind].forEach(depId => {
                const dependents = normalizedReadDependents[depKind].get(depId)
                if (!dependents) return
                dependents.delete(readKey)
                if (dependents.size === 0) normalizedReadDependents[depKind].delete(depId)
            })
        })
    }

    if (kind === 'answer') {
        normalizedAnswerReadCache.delete(id)
    } else {
        normalizedQuestReadCache.delete(id)
    }
}

function indexNormalizedReadCacheEntry(readKey: NormalizedReadCacheKey, deps: EntityCacheIdSets) {
    ;(Object.keys(deps) as EntityCacheKind[]).forEach(depKind => {
        deps[depKind].forEach(depId => {
            let dependents = normalizedReadDependents[depKind].get(depId)
            if (!dependents) {
                dependents = new Set<NormalizedReadCacheKey>()
                normalizedReadDependents[depKind].set(depId, dependents)
            }
            dependents.add(readKey)
        })
    })
}

function setNormalizedAnswerReadCache(
    answerId: string,
    source: Answer,
    value: Answer,
    deps: EntityCacheIdSets,
) {
    const readKey = makeNormalizedReadCacheKey('answer', answerId)
    if (!readKey) return
    deleteNormalizedReadCacheEntry(readKey)
    const entryDeps = cloneEntityCacheIdSets(deps)
    normalizedAnswerReadCache.set(answerId, { source, value, deps: entryDeps })
    indexNormalizedReadCacheEntry(readKey, entryDeps)
}

function setNormalizedQuestReadCache(
    questId: string,
    source: QuestWithAncestors,
    value: QuestWithAncestors,
    deps: EntityCacheIdSets,
) {
    const readKey = makeNormalizedReadCacheKey('quest', questId)
    if (!readKey) return
    deleteNormalizedReadCacheEntry(readKey)
    const entryDeps = cloneEntityCacheIdSets(deps)
    normalizedQuestReadCache.set(questId, { source, value, deps: entryDeps })
    indexNormalizedReadCacheEntry(readKey, entryDeps)
}

function invalidateNormalizedReadCachesForEntity(kind: EntityCacheKind, idValue: unknown) {
    const id = normalizedEntityId(idValue)
    if (!id) return

    const dependents = normalizedReadDependents[kind].get(id)
    if (!dependents?.size) return

    Array.from(dependents).forEach(readKey => deleteNormalizedReadCacheEntry(readKey))
}

function invalidateNormalizedReadCachesFromKey(key: unknown) {
    const kind = getEntityCacheKind(key)
    if (!kind || !Array.isArray(key)) return
    invalidateNormalizedReadCachesForEntity(kind, key[1])
}

function syncEntityTouchMapsWithQueryCache(queries: any[]): EntityCacheSnapshot {
    const snapshot = {
        existingIds: createEntityCacheIdSets(),
        observedIds: createEntityCacheIdSets(),
        queryHashes: {
            answer: new Map<string, string>(),
            quest: new Map<string, string>(),
            userNorm: new Map<string, string>(),
        },
    }

    try {
        queries.forEach(query => {
            const kind = getEntityCacheKind(query?.queryKey)
            if (!kind) return

            const id = normalizedEntityId(query.queryKey[1])
            if (!id) return

            snapshot.existingIds[kind].add(id)
            if (typeof query.queryHash === 'string') {
                snapshot.queryHashes[kind].set(id, query.queryHash)
            }
            if (Number(query?.getObserversCount?.() || 0) > 0) {
                snapshot.observedIds[kind].add(id)
            }
            if (!entityCacheTouchedAt[kind].has(id)) {
                entityCacheTouchedAt[kind].set(id, Date.now())
            }
        })
    } catch (error) {}

    ;(Object.keys(entityCacheTouchedAt) as EntityCacheKind[]).forEach(kind => {
        Array.from(entityCacheTouchedAt[kind].keys()).forEach(id => {
            if (!snapshot.existingIds[kind].has(id)) entityCacheTouchedAt[kind].delete(id)
        })
        Array.from(entityCacheRetainedUntil[kind].entries()).forEach(([id, retainedUntil]) => {
            if (!snapshot.existingIds[kind].has(id) || retainedUntil <= Date.now()) {
                entityCacheRetainedUntil[kind].delete(id)
            }
        })
    })

    return snapshot
}

function collectAnswerRefsFromRecord(
    answer: Partial<Answer> | null | undefined,
    protectedAnswerIds: Set<string>,
    protectedQuestIds: Set<string>,
    protectedUserIds: Set<string>,
    answerQueue: string[],
    questQueue: string[],
) {
    if (!answer) return

    const userId = normalizedEntityId(answer.user_id || answer.user?.id)
    if (userId) protectedUserIds.add(userId)

    const draftAnswerId = normalizedEntityId(answer.draft_answer_id)
    if (draftAnswerId && !protectedAnswerIds.has(draftAnswerId)) {
        protectedAnswerIds.add(draftAnswerId)
        answerQueue.push(draftAnswerId)
    }

    if (Array.isArray(answer.embeds)) {
        answer.embeds.forEach(embed => {
            const embedId =
                typeof embed === 'object'
                    ? normalizedEntityId(embed?.id)
                    : normalizedEntityId(embed)
            if (!embedId || protectedAnswerIds.has(embedId)) return
            protectedAnswerIds.add(embedId)
            answerQueue.push(embedId)
        })
    }

    if (Array.isArray(answer.child_quests)) {
        answer.child_quests.forEach(quest => {
            const questId =
                typeof quest === 'object'
                    ? normalizedEntityId(quest?.id)
                    : normalizedEntityId(quest)
            if (!questId || protectedQuestIds.has(questId)) return
            protectedQuestIds.add(questId)
            questQueue.push(questId)
        })
    }
}

function collectQuestRefsFromRecord(
    quest: Partial<QuestWithAncestors> | null | undefined,
    protectedAnswerIds: Set<string>,
    answerQueue: string[],
    protectTailAnswers = true,
) {
    if (!quest) return
    ;[quest.parent_id, quest.grand_parent_id, quest.parent?.id, quest.grand_parent?.id].forEach(
        answerIdValue => {
            const answerId = normalizedEntityId(answerIdValue)
            if (!answerId || protectedAnswerIds.has(answerId)) return
            protectedAnswerIds.add(answerId)
            answerQueue.push(answerId)
        },
    )

    if (protectTailAnswers && Array.isArray(quest.sorted_answer_ids)) {
        quest.sorted_answer_ids.forEach(answerIdValue => {
            const answerId = normalizedEntityId(answerIdValue)
            if (!answerId || protectedAnswerIds.has(answerId)) return
            protectedAnswerIds.add(answerId)
            answerQueue.push(answerId)
        })
    }

    if (protectTailAnswers && Array.isArray(quest.sorted_answers)) {
        quest.sorted_answers.forEach(answer => {
            const answerId = normalizedEntityId(answer?.id)
            if (!answerId || protectedAnswerIds.has(answerId)) return
            protectedAnswerIds.add(answerId)
            answerQueue.push(answerId)
        })
    }
}

function enqueueProtectedQuest(
    questIdValue: unknown,
    protectedQuestIds: Set<string>,
    questQueue: string[],
) {
    const questId = normalizedEntityId(questIdValue)
    if (!questId || protectedQuestIds.has(questId)) return

    protectedQuestIds.add(questId)
    questQueue.push(questId)
}

function streamQueryProtectsEntities(query: any): boolean {
    return query?.options?.meta?.protectsStreamEntities !== false
}

function collectProtectedEntityIds(queries: any[]) {
    const protectedAnswerIds = new Set<string>()
    const protectedQuestIds = new Set<string>()
    const protectedUserIds = new Set<string>()
    const answerQueue: string[] = []
    const questQueue: string[] = []
    const questIdsWithoutTailProtection = new Set<string>()

    getSubscribedEntityIds('answer').forEach(id => {
        protectedAnswerIds.add(id)
        answerQueue.push(id)
    })
    getSubscribedEntityIds('quest').forEach(id => {
        protectedQuestIds.add(id)
        questQueue.push(id)
    })
    getSubscribedEntityIds('userNorm').forEach(id => protectedUserIds.add(id))

    try {
        queries.forEach(query => {
            if (Number(query?.getObserversCount?.() || 0) <= 0) return

            const kind = getEntityCacheKind(query?.queryKey)
            if (!kind) return

            const id = normalizedEntityId(query.queryKey[1])
            if (!id) return

            if (kind === 'answer') {
                protectedAnswerIds.add(id)
                answerQueue.push(id)
            } else if (kind === 'quest') {
                protectedQuestIds.add(id)
                questQueue.push(id)
                if (query?.options?.meta?.protectsQuestAnswerEntities === false) {
                    questIdsWithoutTailProtection.add(id)
                }
            } else {
                protectedUserIds.add(id)
            }
        })
    } catch (error) {}

    queries
        .filter(query => query?.queryKey?.[0] === 'quests' && streamQueryProtectsEntities(query))
        .forEach(query => {
            const data = query.state?.data
            const typedData = data as PaginatedQuestsData | null
            typedData?.pages?.forEach(page => {
                page.quests?.forEach(questRef => {
                    enqueueProtectedQuest(questRef?.id, protectedQuestIds, questQueue)
                })
                enqueueProtectedQuest(page.pinnedQuest?.id, protectedQuestIds, questQueue)
                enqueueProtectedQuest(page.pinned_quest?.id, protectedQuestIds, questQueue)
            })
        })

    queries
        .filter(query => query?.queryKey?.[0] === 'actions')
        .forEach(query => {
            const data = query.state?.data
            const typedData = data as PaginatedActionsData | null
            typedData?.pages?.forEach(page => {
                page.actions?.forEach(action => {
                    enqueueProtectedQuest(action?.quest?.id, protectedQuestIds, questQueue)
                })
            })
        })

    let questQueueIndex = 0
    let answerQueueIndex = 0
    while (questQueueIndex < questQueue.length || answerQueueIndex < answerQueue.length) {
        const questId =
            questQueueIndex < questQueue.length ? questQueue[questQueueIndex++] : undefined
        if (questId) {
            collectQuestRefsFromRecord(
                peekQueryData<QuestWithAncestors>(entityQueryKey('quest', questId)),
                protectedAnswerIds,
                answerQueue,
                !questIdsWithoutTailProtection.has(questId),
            )
        }

        const answerId =
            answerQueueIndex < answerQueue.length ? answerQueue[answerQueueIndex++] : undefined
        if (answerId) {
            collectAnswerRefsFromRecord(
                peekQueryData<Answer>(entityQueryKey('answer', answerId)),
                protectedAnswerIds,
                protectedQuestIds,
                protectedUserIds,
                answerQueue,
                questQueue,
            )
        }
    }

    return {
        answer: protectedAnswerIds,
        quest: protectedQuestIds,
        userNorm: protectedUserIds,
    }
}

// QA-only override so cache-pressure scenarios (entity eviction under long scroll) can be
// reproduced deterministically without a production-length session:
//   localStorage.setItem('knov_qa_entity_cache_limit_answer', '40')
function entityCacheLimit(kind: EntityCacheKind): number {
    try {
        const raw =
            typeof window !== 'undefined'
                ? window.localStorage?.getItem(`knov_qa_entity_cache_limit_${kind}`)
                : null
        const override = Number(raw)
        if (Number.isFinite(override) && override > 0) return override
    } catch {}
    return ENTITY_CACHE_LIMITS[kind]
}

function entityCacheIsOverLimit(): boolean {
    return (Object.keys(ENTITY_CACHE_LIMITS) as EntityCacheKind[]).some(
        kind => entityCacheTouchedAt[kind].size > entityCacheLimit(kind),
    )
}

function getEntityCacheQueries(): any[] {
    try {
        return (queryClient.getQueryCache() as any).getAll()
    } catch {
        return []
    }
}

function createEntityCachePrunePlan(): EntityCachePrunePlan | null {
    const queries = getEntityCacheQueries()
    const snapshot = syncEntityTouchMapsWithQueryCache(queries)
    if (!entityCacheIsOverLimit()) return null

    const protectedIds = collectProtectedEntityIds(queries)
    const candidates: EntityCachePruneCandidate[] = []
    ;(Object.keys(ENTITY_CACHE_LIMITS) as EntityCacheKind[]).forEach(kind => {
        const touchedAt = entityCacheTouchedAt[kind]
        let remaining = Math.max(0, touchedAt.size - entityCacheLimit(kind))
        if (!remaining) return

        for (const [id] of touchedAt) {
            if (!remaining) break
            const queryHash = snapshot.queryHashes[kind].get(id)
            if (
                protectedIds[kind].has(id) ||
                entityCacheRetainedUntil[kind].has(id) ||
                !snapshot.existingIds[kind].has(id) ||
                snapshot.observedIds[kind].has(id) ||
                !queryHash
            ) {
                continue
            }
            candidates.push({ kind, id, queryHash })
            remaining -= 1
        }
    })

    if (!candidates.length) return null
    return {
        candidates,
        cursor: 0,
        normalizedReadCachesCleared: false,
        revision: entityCachePruneRevision,
    }
}

function resolveDetachedEntityCachePruneQuery(candidate: EntityCachePruneCandidate) {
    if (entityCacheRetainedUntil[candidate.kind].has(candidate.id)) return null
    if (isCachedEntitySubscribed(candidate.kind, candidate.id)) return null

    const query = queryClient.getQueryCache().get(candidate.queryHash)
    if (!query) {
        entityCacheTouchedAt[candidate.kind].delete(candidate.id)
        return null
    }
    if (Number(query.getObserversCount?.() || 0) > 0) return null
    return query
}

function runEntityCachePruneSlice(
    plan: EntityCachePrunePlan,
    deadline?: EntityCacheIdleDeadline,
    maxCandidates = ENTITY_CACHE_PRUNE_MAX_CANDIDATES_PER_SLICE,
) {
    let visited = 0
    while (plan.cursor < plan.candidates.length && visited < maxCandidates) {
        if (
            visited > 0 &&
            deadline &&
            deadline.timeRemaining() < ENTITY_CACHE_PRUNE_MIN_IDLE_TIME_MS
        ) {
            break
        }

        const candidate = plan.candidates[plan.cursor++]
        visited += 1
        const query = resolveDetachedEntityCachePruneQuery(candidate)
        if (!query) continue

        if (!plan.normalizedReadCachesCleared) {
            clearNormalizedReadCaches()
            plan.normalizedReadCachesCleared = true
        }
        queryClient.getQueryCache().remove(query)
        entityCacheTouchedAt[candidate.kind].delete(candidate.id)
    }

    return plan.cursor >= plan.candidates.length
}

function cancelScheduledEntityCachePrune() {
    const idleWindow = typeof window !== 'undefined' ? (window as EntityCacheIdleWindow) : null
    if (entityCachePruneHandle != null) {
        if (entityCachePruneHandleKind === 'idle') {
            idleWindow?.cancelIdleCallback?.(entityCachePruneHandle)
        } else {
            clearTimeout(entityCachePruneHandle)
        }
    }
    entityCachePruneHandle = null
    entityCachePruneHandleKind = null
    entityCachePruneScrollUnsubscribe?.()
    entityCachePruneScrollUnsubscribe = null
}

export function flushEntityCachePruneForTests() {
    cancelScheduledEntityCachePrune()
    entityCachePrunePlan = createEntityCachePrunePlan()
    while (entityCachePrunePlan) {
        const complete = runEntityCachePruneSlice(
            entityCachePrunePlan,
            undefined,
            Number.MAX_SAFE_INTEGER,
        )
        if (complete) entityCachePrunePlan = null
    }
}

export function runEntityCachePruneSliceForTests(timeRemainingMs = 50) {
    cancelScheduledEntityCachePrune()
    if (!entityCachePrunePlan || entityCachePrunePlan.revision !== entityCachePruneRevision) {
        entityCachePrunePlan = createEntityCachePrunePlan()
    }
    if (!entityCachePrunePlan) return false

    if (
        runEntityCachePruneSlice(entityCachePrunePlan, {
            timeRemaining: () => timeRemainingMs,
        })
    ) {
        entityCachePrunePlan = null
    }
    return entityCachePrunePlan != null
}

function waitForEntityCachePruneScrollIdle() {
    if (entityCachePruneScrollUnsubscribe) return
    entityCachePruneScrollUnsubscribe = subscribeNaturalFeedScrollActivity(() => {
        if (naturalFeedScrollIsActive()) return

        const unsubscribe = entityCachePruneScrollUnsubscribe
        entityCachePruneScrollUnsubscribe = null
        unsubscribe?.()
        scheduleDetachedEntityPrune()
    })
}

function scheduleDetachedEntityPrune() {
    if (entityCachePruneHandle != null) return
    if (naturalFeedScrollIsActive()) {
        waitForEntityCachePruneScrollIdle()
        return
    }

    const run = (deadline?: EntityCacheIdleDeadline) => {
        entityCachePruneHandle = null
        entityCachePruneHandleKind = null
        if (naturalFeedScrollIsActive()) {
            waitForEntityCachePruneScrollIdle()
            return
        }

        if (!entityCachePrunePlan || entityCachePrunePlan.revision !== entityCachePruneRevision) {
            entityCachePrunePlan = createEntityCachePrunePlan()
        }
        if (!entityCachePrunePlan) return

        if (runEntityCachePruneSlice(entityCachePrunePlan, deadline)) {
            entityCachePrunePlan = null
            return
        }
        scheduleDetachedEntityPrune()
    }
    const idleWindow = typeof window !== 'undefined' ? (window as EntityCacheIdleWindow) : null

    if (idleWindow?.requestIdleCallback) {
        entityCachePruneHandleKind = 'idle'
        entityCachePruneHandle = idleWindow.requestIdleCallback(run)
    } else {
        entityCachePruneHandleKind = 'timeout'
        entityCachePruneHandle = setTimeout(
            () => run(),
            ENTITY_CACHE_PRUNE_FALLBACK_DELAY_MS,
        ) as unknown as number
    }
}

function recordEntityCacheWrite(
    kind: EntityCacheKind,
    id: unknown,
    options = { skipPrune: false },
) {
    touchEntityCache(kind, id)
    entityCachePruneRevision += 1
    if (options.skipPrune) return

    entityCacheWriteCount += 1
    if (entityCacheWriteCount % ENTITY_CACHE_PRUNE_INTERVAL_WRITES !== 0) return

    scheduleDetachedEntityPrune()
}

function recordEntityCacheWriteFromKey(
    key: unknown,
    options: { skipEntityRetentionPrune?: boolean } = {},
) {
    const kind = getEntityCacheKind(key)
    if (!kind || !Array.isArray(key)) return

    recordEntityCacheWrite(kind, key[1], {
        skipPrune: options.skipEntityRetentionPrune === true,
    })
}

function sameShallowRecordValues(
    left: Record<string, unknown> | null | undefined,
    right: Record<string, unknown> | null | undefined,
): boolean {
    if (left === right) return true
    if (!left || !right) return false

    const leftKeys = Object.keys(left)
    const rightKeys = Object.keys(right)
    if (leftKeys.length !== rightKeys.length) return false

    return leftKeys.every(key => Object.is(left[key], right[key]))
}

function reportQuestCacheAnomaly(event: string, payload: Record<string, unknown>) {
    const signature = JSON.stringify({ event, ...payload })
    const now = Date.now()
    const lastSeenAt = questCacheAnomalySeenAt.get(signature)
    if (lastSeenAt && now - lastSeenAt < QUEST_CACHE_ANOMALY_TTL_MS) return

    questCacheAnomalySeenAt.set(signature, now)
    console.warn(`[quest-cache] ${event}`, payload)

    try {
        Sentry.withScope(scope => {
            scope.setLevel('warning')
            scope.setExtras(payload)
            Sentry.captureMessage(`[quest-cache] ${event}`)
        })
    } catch (error) {}

    try {
        ;(window as any).logEv?.(`QUEST_CACHE_${event.toUpperCase()}`, payload)
    } catch (error) {}
}

// Global deduplication for user bulk-fetch requests.
// Prevents multiple UserIcon instances from issuing duplicate requests for the same user.
const pendingUserFetches = new Set<string>()

export function isUserFetchPending(userId: string | number): boolean {
    return pendingUserFetches.has(String(userId))
}

export function markUserFetchPending(userId: string | number): void {
    pendingUserFetches.add(String(userId))
}

export function clearUserFetchPending(userId: string | number): void {
    pendingUserFetches.delete(String(userId))
}

function set(key, data, options: { skipEntityRetentionPrune?: boolean } = {}) {
    invalidateNormalizedReadCachesFromKey(key)
    queryClient.setQueryData(key, data)
    recordEntityCacheWriteFromKey(key, options)
}

export function getCachedUser(userId: string | number | null | undefined): CachedUser | null {
    if (userId === null || userId === undefined) return null
    const id = String(userId)
    return peekQueryData<CachedUser>(userNormKey(id)) ?? null
}

export function cacheUser(user: User | Record<string, unknown>, update_timestamp: boolean = true) {
    if (!user || user.id === null || user.id === undefined) return null
    const id = String(user.id)
    const existing = peekQueryData<CachedUser>(userNormKey(id)) ?? null

    // IMPORTANT:
    // - when update_timestamp=true, we treat incoming data as authoritative (e.g. from /users/:id).
    // - when update_timestamp=false, we treat incoming data as "best effort" (e.g. embedded user on an Answer)
    //   and do NOT allow it to clobber fresher cached fields like name/avatar.
    //
    // Race condition protection: if existing data has a recent __cache_ts (within last 5s) and
    // we're doing a non-authoritative update, skip the write entirely to avoid clobbering
    // fresh data that was just written by an authoritative call.
    if (!update_timestamp && existing?.__cache_ts) {
        const age = Date.now() - existing.__cache_ts
        if (age < 5000) {
            // Existing data is very fresh; don't clobber it with potentially stale embedded data
            return user
        }
    }

    const merged = update_timestamp
        ? { ...(existing || {}), ...user, __cache_ts: Date.now() }
        : { ...user, ...(existing || {}) }

    if (sameShallowRecordValues(existing, merged)) return user

    set(userNormKey(id), merged)
    return user
}

export function cacheUsers(users: (User | Record<string, unknown>)[]) {
    if (!users || users.length === 0) return
    users.forEach(u => cacheUser(u))
}

// Resolve a user object for rendering:
// - Prefer the freshest fields from the passed-in object (e.g. from an API response)
// - Fill missing fields from the normalized user cache
// - Always prefer cached avatar fields, so avatar updates propagate without rewriting answers
export function resolveUser(
    userOrId: User | Record<string, unknown> | string | number | null | undefined,
    fallbackUserId?: string | number | null,
): CachedUser | null {
    const base = userOrId && typeof userOrId === 'object' ? userOrId : null

    const id =
        (base as User | null)?.id ||
        (typeof userOrId === 'string' || typeof userOrId === 'number' ? userOrId : null) ||
        fallbackUserId
    if (!id) return base as CachedUser | null

    const cached = getCachedUser(id)
    if (!cached) return (base as CachedUser) || null

    // Cached data should win for volatile fields (avatar/name), but preserve any extra
    // fields present on the passed-in object (e.g. one-off flags).
    const merged: CachedUser = {
        ...(base || {}),
        ...(cached || {}),
    } as CachedUser

    // Avatar fields are the primary volatile data; always prefer cached if present.
    if (cached?.avatar_blob_id) merged.avatar_blob_id = cached.avatar_blob_id
    if (cached?.avatar_attachment_id) merged.avatar_attachment_id = cached.avatar_attachment_id
    if (cached?.avatar_urls) merged.avatar_urls = cached.avatar_urls

    return merged
}

function touchQuestInStreamQueries(questId: string) {
    queryClient
        .getQueryCache()
        .getAll()
        .filter(query => query?.queryKey?.[0] === 'quests' && streamQueryProtectsEntities(query))
        .forEach(query => {
            const queryKey = query.queryKey
            const data = query.state?.data
            const typedData = data as PaginatedQuestsData | null
            if (!typedData?.pages) return

            let hasUpdates = false
            const updatedPages = typedData.pages.map((page: QuestsPage) => {
                if (!page?.quests) return page

                let pageUpdated = false
                const updatedQuests = page.quests.map(questRef => {
                    if (questRef?.id === questId) {
                        pageUpdated = true
                        return { ...questRef }
                    }
                    return questRef
                })

                if (pageUpdated) {
                    hasUpdates = true
                    return { ...page, quests: updatedQuests }
                }

                return page
            })

            if (hasUpdates) {
                queryClient.setQueryData(queryKey, {
                    ...typedData,
                    pages: updatedPages,
                })
            }
        })
}

export function getCachedAnswer(
    answerId: string,
    visited: Set<string> = new Set(),
    deps: EntityCacheIdSets = createEntityCacheIdSets(),
): Answer {
    const normalizedAnswerId = normalizedEntityId(answerId)
    if (!normalizedAnswerId || visited.has(normalizedAnswerId)) return null
    if (isPrivateAnswerPlaceholderId(answerId)) return { id: '__PRIVATE__' } as Answer
    const canUseReadCache = visited.size === 0

    visited.add(normalizedAnswerId)

    addNormalizedReadDependency(deps, 'answer', normalizedAnswerId)
    const answer = peekQueryData(['answer', normalizedAnswerId]) as Answer
    if (!answer) return null
    if (canUseReadCache) {
        const cached = normalizedAnswerReadCache.get(normalizedAnswerId)
        if (cached?.source === answer) return cached.value
    }

    // Create a shallow copy to avoid mutating cached data
    const result = { ...answer }

    // Normalize user: prefer the shared user cache when available.
    // Backwards compatible with payloads that embed full user objects.
    try {
        const embeddedUser = answer.user
        const uid = embeddedUser?.id || answer.user_id
        if (uid) {
            addNormalizedReadDependency(deps, 'userNorm', uid)
            const cached = getCachedUser(uid)
            if (cached) result.user = cached
        }
    } catch {}

    // Handle embeds
    if (answer.embeds && Array.isArray(answer.embeds)) {
        result.embeds = answer.embeds
            .filter(embed => embed != null) // Filter out null/undefined embeds
            .map(embed => {
                if (typeof embed === 'object' && embed.id) {
                    if (isPrivateAnswerPlaceholderId(embed.id)) return { id: '__PRIVATE__' }
                    return getCachedAnswer(embed.id, visited, deps) || embed
                }
                // If embed is just an id string or number
                if (embed) {
                    if (isPrivateAnswerPlaceholderId(embed)) return { id: '__PRIVATE__' }
                    const embedId = normalizedEntityId(embed)
                    addNormalizedReadDependency(deps, 'answer', embedId)
                    return embedId ? peekQueryData(['answer', embedId]) : null
                }
                return null
            })
            .filter((embed): embed is Embed => embed != null) // Remove any nulls from the result
    }

    // Handle draft answer
    if (answer.draft_answer_id) {
        result.draft_answer = getCachedAnswer(answer.draft_answer_id, visited, deps)
    }

    // Handle child quests to prevent circular reference
    if (answer.child_quests && Array.isArray(answer.child_quests)) {
        result.child_quests = answer.child_quests
            .filter(quest => quest != null) // Filter out null/undefined quests
            .map(quest => {
                if (typeof quest === 'object' && quest.id) {
                    // Try to get the cached quest, but if it's not found, return the original quest object
                    const cachedQuest = getCachedQuest(quest.id, visited, deps)
                    return cachedQuest || quest
                }
                addNormalizedReadDependency(deps, 'quest', quest)
                return quest
            })
    }

    adoptAnswerGeometryProjection(result, primeAnswerGeometryProjection(answer))
    adoptEmbeddedAnswerGeometryProjection(result, resolveEmbeddedAnswerGeometryProjection(answer))

    if (canUseReadCache) {
        setNormalizedAnswerReadCache(normalizedAnswerId, answer, result, deps)
    }
    return result
}

export function cacheAnswers(answers: AnswerOrDraft[]) {
    answers.map(answer => cacheAnswer(answer))
}

function hasArrayValues(value: unknown): value is unknown[] {
    return Array.isArray(value) && value.length > 0
}

function hasObjectValues(value: unknown): boolean {
    return (
        !!value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value).length > 0
    )
}

function hasNonBlankString(value: unknown): boolean {
    return typeof value === 'string' && value.trim().length > 0
}

function cloneAnswerCacheField<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map(entry =>
            entry && typeof entry === 'object' && !Array.isArray(entry) ? { ...entry } : entry,
        ) as T
    }

    if (value instanceof Date) {
        return new Date(value.getTime()) as T
    }

    if (value && typeof value === 'object') {
        return { ...value } as T
    }

    return value
}

type PartialAnswerCacheEntry = Partial<Answer> & Record<string, unknown>
type PartialUrlCacheEntry = Partial<UrlModel> & Record<string, unknown>
type AnswerCacheEntryRecord = Record<string, unknown> & { id?: unknown; stub?: boolean }
type UpvalueCacheEntry = {
    id?: unknown
    amount?: unknown
    is_synthetic?: unknown
    pending_queue_id?: unknown
    coalesced_upvalue_id?: unknown
}
type CacheAnswerOptions = {
    preservePendingSyntheticUpvalues?: boolean
    skipEntityRetentionPrune?: boolean
}

const QUEST_VIEW_CONTEXT_FIELDS = [
    'matching_answer_ids',
    'notification_reason_label',
    'not_acted_answer_ids',
    'has_acted',
    'noti_ids',
    'participants_key_count',
] as const

function stringId(value: unknown): string | null {
    return value == null ? null : String(value)
}

function hasOwnKey(obj: unknown, key: string): boolean {
    return !!obj && Object.prototype.hasOwnProperty.call(obj, key)
}

function isEmptyIncomingViewContextValue(value: unknown): boolean {
    if (Array.isArray(value)) return value.length === 0
    return value === null || value === undefined
}

function hasPreservableExistingViewContextValue(value: unknown): boolean {
    if (Array.isArray(value)) return value.length > 0
    return value !== null && value !== undefined
}

function preserveQuestViewContextFields(
    incomingQuest: QuestWithAncestors,
    existingQuest: QuestWithAncestors | null,
): QuestWithAncestors {
    if (!existingQuest) return incomingQuest

    let nextQuest = incomingQuest
    QUEST_VIEW_CONTEXT_FIELDS.forEach(key => {
        if (!hasOwnKey(incomingQuest, key)) return

        const incomingValue = (incomingQuest as unknown as Record<string, unknown>)[key]
        const existingValue = (existingQuest as unknown as Record<string, unknown>)[key]
        if (!isEmptyIncomingViewContextValue(incomingValue)) return
        if (!hasPreservableExistingViewContextValue(existingValue)) return

        if (nextQuest === incomingQuest) nextQuest = { ...incomingQuest }
        ;(nextQuest as unknown as Record<string, unknown>)[key] =
            cloneAnswerCacheField(existingValue)
    })

    return nextQuest
}

function upvalueCacheEntry(value: unknown): UpvalueCacheEntry | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null
    return value as UpvalueCacheEntry
}

function isSyntheticUpvalue(value: unknown): boolean {
    const upvalue = upvalueCacheEntry(value)
    if (!upvalue) return false

    return (
        upvalue.is_synthetic === true ||
        stringId(upvalue.pending_queue_id) != null ||
        stringId(upvalue.id)?.startsWith('synthetic-') === true
    )
}

function numericAmount(value: unknown): number | null {
    const amount = Number(value)
    return Number.isFinite(amount) ? amount : null
}

function upvalueAmount(upvalue: UpvalueCacheEntry): number {
    return numericAmount(upvalue.amount) ?? 0
}

function addUpvalueIdentityKeys(keys: Set<string>, upvalue: UpvalueCacheEntry) {
    const id = stringId(upvalue.id)
    const pendingQueueId = stringId(upvalue.pending_queue_id)
    const coalescedUpvalueId = stringId(upvalue.coalesced_upvalue_id)

    if (id) keys.add(`id:${id}`)
    if (pendingQueueId) keys.add(`pending:${pendingQueueId}`)
    if (coalescedUpvalueId) keys.add(`coalesced:${coalescedUpvalueId}`)
}

function hasAnyUpvalueIdentityKey(keys: Set<string>, upvalue: UpvalueCacheEntry): boolean {
    const ownKeys = new Set<string>()
    addUpvalueIdentityKeys(ownKeys, upvalue)
    return Array.from(ownKeys).some(key => keys.has(key))
}

function syntheticUpvalueCoveredByIncomingRealUpvalue(
    syntheticUpvalue: UpvalueCacheEntry,
    incomingRealUpvalueIds: Set<string>,
): boolean {
    const coalescedUpvalueId = stringId(syntheticUpvalue.coalesced_upvalue_id)
    const pendingQueueId = stringId(syntheticUpvalue.pending_queue_id)

    return (
        (!!coalescedUpvalueId && incomingRealUpvalueIds.has(coalescedUpvalueId)) ||
        (!!pendingQueueId && incomingRealUpvalueIds.has(pendingQueueId))
    )
}

function preserveUnresolvedSyntheticUpvalues(
    mutableEntry: PartialAnswerCacheEntry,
    existingEntry: PartialAnswerCacheEntry | null | undefined,
) {
    const existingUpvalues = Array.isArray(existingEntry?.bsv_upvalues)
        ? existingEntry.bsv_upvalues
        : []
    const existingSyntheticUpvalues = existingUpvalues.filter(isSyntheticUpvalue)
    if (!existingSyntheticUpvalues.length) return

    const incomingAmount = numericAmount(mutableEntry.bsv_upvalue_amt)
    if (incomingAmount == null) return

    if (!Array.isArray(mutableEntry.bsv_upvalues)) return

    const incomingUpvalues = mutableEntry.bsv_upvalues
    const incomingIdentityKeys = new Set<string>()
    const incomingRealUpvalueIds = new Set<string>()

    incomingUpvalues.forEach(upvalue => {
        const entry = upvalueCacheEntry(upvalue)
        if (!entry) return

        addUpvalueIdentityKeys(incomingIdentityKeys, entry)
        if (!isSyntheticUpvalue(entry)) {
            const id = stringId(entry.id)
            if (id) incomingRealUpvalueIds.add(id)
        }
    })

    const unresolvedSyntheticUpvalues = existingSyntheticUpvalues.filter(upvalue => {
        if (hasAnyUpvalueIdentityKey(incomingIdentityKeys, upvalue)) return false
        return !syntheticUpvalueCoveredByIncomingRealUpvalue(upvalue, incomingRealUpvalueIds)
    })

    if (!unresolvedSyntheticUpvalues.length) return

    mutableEntry.bsv_upvalues = [
        ...incomingUpvalues,
        ...(cloneAnswerCacheField(
            unresolvedSyntheticUpvalues,
        ) as typeof unresolvedSyntheticUpvalues),
    ]
    mutableEntry.bsv_upvalue_amt =
        incomingAmount +
        unresolvedSyntheticUpvalues.reduce((sum, upvalue) => sum + upvalueAmount(upvalue), 0)
}

export function answerRenderablePayloadFingerprint(answer: unknown): string[] {
    const entry = answer as AnswerCacheEntryRecord | null | undefined
    if (!entry || typeof entry !== 'object') return []

    const fingerprint: string[] = []
    if (entry.deleted) fingerprint.push('deleted')
    if (hasNonBlankString(entry.content)) fingerprint.push('content')
    if (hasNonBlankString(entry.display_content)) fingerprint.push('display_content')
    if (hasNonBlankString(entry.content_preview)) fingerprint.push('content_preview')
    if (hasObjectValues(entry.delta_json)) fingerprint.push('delta_json')
    if (hasObjectValues(entry.nested_delta_json)) fingerprint.push('nested_delta_json')
    if (hasObjectValues(entry.url)) fingerprint.push('url')
    if (entry.answer_image_url) fingerprint.push('answer_image_url')
    if (entry.local_answer_image_url) fingerprint.push('local_answer_image_url')
    if (entry.recording_url) fingerprint.push('recording_url')
    if (entry.mp4_recording_url) fingerprint.push('mp4_recording_url')
    if (entry.recording_thumbnail_url) fingerprint.push('recording_thumbnail_url')
    if (hasArrayValues(entry.images_v2)) fingerprint.push('images_v2')
    if (hasArrayValues(entry.image_urls)) fingerprint.push('image_urls')
    if (hasArrayValues(entry.file_urls)) fingerprint.push('file_urls')
    if (hasArrayValues(entry.embeds)) fingerprint.push('embeds')
    if (hasArrayValues(entry.child_quests)) fingerprint.push('child_quests')
    if (hasArrayValues(entry.ai_runs)) fingerprint.push('ai_runs')
    if (entry.message_type === 'poll' && hasArrayValues(entry.poll_options_payload)) {
        fingerprint.push('poll_options_payload')
    }

    return fingerprint
}

function answerCacheEntryHasRenderableData(entry: AnswerCacheEntryRecord): boolean {
    return answerRenderablePayloadFingerprint(entry).length > 0
}

export function isAnswerStubCacheEntry(answer: unknown): boolean {
    const entry = answer as AnswerCacheEntryRecord | null | undefined
    return !!entry?.id && (entry.stub === true || entry._thread_transport_stub === true)
}

function markAnswerStubState<T extends AnswerCacheEntryRecord>(entry: T): T {
    if (answerCacheEntryHasRenderableData(entry)) {
        delete entry.stub
    } else {
        entry.stub = true
    }

    return entry
}

function redactVolatileMediaUrl(value: string): string {
    if (!/[?&](?:vtoken|X-Amz-|Signature=|Expires=|Key-Pair-Id=)/i.test(value)) return value

    try {
        const url = new URL(value, 'http://treechat.local')
        const volatileParams = [
            'vtoken',
            'X-Amz-Algorithm',
            'X-Amz-Credential',
            'X-Amz-Date',
            'X-Amz-Expires',
            'X-Amz-Security-Token',
            'X-Amz-Signature',
            'X-Amz-SignedHeaders',
            'Signature',
            'Expires',
            'Key-Pair-Id',
        ]
        volatileParams.forEach(param => {
            if (url.searchParams.has(param)) url.searchParams.set(param, '<volatile>')
        })

        const normalized = url.toString()
        return value.startsWith('http')
            ? normalized
            : normalized.replace(/^http:\/\/treechat\.local/, '')
    } catch {
        return value.replace(
            /([?&])((?:vtoken|X-Amz-[^=&#]+|Signature|Expires|Key-Pair-Id)=)[^&#]*/gi,
            '$1$2<volatile>',
        )
    }
}

const VOLATILE_CACHE_KEYS = new Set([
    '__cache_ts',
    'image_visibility_token',
    'media_visibility_token',
])
const COMPACT_ANSWER_KEYS = [
    'archive',
    'deleted',
    'created_at',
    'position',
    'quest_id',
    'reply_to_answer_id',
    'child_quests',
] as const
const COMPACT_PARENT_KEYS = [
    'id',
    'archive',
    'created_at',
    'position',
    'quest_id',
    'updated_at',
    'content',
    'delta_json',
    'nested_delta_json',
    'images_v2',
    'image_urls',
    'file_urls',
    'ai_runs',
] as const

const isComparableRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value)

const isJsonOmittedObjectValue = (value: unknown) =>
    value === undefined || typeof value === 'function' || typeof value === 'symbol'

function stableCacheValuesEqual(
    left: unknown,
    right: unknown,
    options: {
        entryKey?: string
        ignoredTopLevelKeys?: Set<string>
        isArrayElement?: boolean
        isTopLevel?: boolean
        leftStack: Set<object>
        rightStack: Set<object>
    },
): boolean {
    const {
        entryKey,
        ignoredTopLevelKeys,
        isArrayElement = false,
        isTopLevel = false,
        leftStack,
        rightStack,
    } = options

    if (entryKey === 'draft_answer_id') return true
    if (left === right) return typeof left !== 'bigint'

    if (isArrayElement) {
        const leftNormalizesToNull =
            left === undefined ||
            typeof left === 'function' ||
            typeof left === 'symbol' ||
            (typeof left === 'number' && !Number.isFinite(left))
        const rightNormalizesToNull =
            right === undefined ||
            typeof right === 'function' ||
            typeof right === 'symbol' ||
            (typeof right === 'number' && !Number.isFinite(right))
        if ((leftNormalizesToNull || left === null) && (rightNormalizesToNull || right === null)) {
            return true
        }
    }

    if (typeof left === 'number' && typeof right === 'number') {
        return !Number.isFinite(left) && !Number.isFinite(right)
    }
    if (
        (typeof left === 'number' && !Number.isFinite(left) && right === null) ||
        (typeof right === 'number' && !Number.isFinite(right) && left === null)
    ) {
        return true
    }
    if (typeof left === 'string' || typeof right === 'string') {
        return (
            typeof left === 'string' &&
            typeof right === 'string' &&
            redactVolatileMediaUrl(left) === redactVolatileMediaUrl(right)
        )
    }
    if (typeof left === 'bigint' || typeof right === 'bigint') return false

    if (entryKey === 'user') {
        const leftUserId = isComparableRecord(left) ? left.id : null
        const rightUserId = isComparableRecord(right) ? right.id : null
        if (leftUserId != null || rightUserId != null) {
            return (
                leftUserId != null &&
                rightUserId != null &&
                String(leftUserId) === String(rightUserId)
            )
        }
    }

    if (entryKey === 'child_quests') {
        const compactLeft =
            Array.isArray(left) && left.every(entry => !entry || typeof entry === 'object')
        const compactRight =
            Array.isArray(right) && right.every(entry => !entry || typeof entry === 'object')
        if (compactLeft || compactRight) {
            if (!compactLeft || !compactRight || left.length !== right.length) return false
            for (let index = 0; index < left.length; index += 1) {
                const entry = left[index]
                const other = right[index]
                const leftId = isComparableRecord(entry) ? entry.id : null
                const rightId = isComparableRecord(other) ? other.id : null
                if (leftId != null || rightId != null) {
                    if (leftId == null || rightId == null || String(leftId) !== String(rightId)) {
                        return false
                    }
                    continue
                }
                if (
                    !stableCacheValuesEqual(entry, other, {
                        leftStack,
                        rightStack,
                        isArrayElement: true,
                    })
                ) {
                    return false
                }
            }
            return true
        }
    }

    if (entryKey === 'sorted_answers') {
        const compactLeft =
            Array.isArray(left) && left.every(entry => !entry || typeof entry === 'object')
        const compactRight =
            Array.isArray(right) && right.every(entry => !entry || typeof entry === 'object')
        if (compactLeft || compactRight) {
            if (!compactLeft || !compactRight || left.length !== right.length) return false
            for (let index = 0; index < left.length; index += 1) {
                const entry = left[index]
                const other = right[index]
                const leftAnswer = isComparableRecord(entry) ? entry : null
                const rightAnswer = isComparableRecord(other) ? other : null
                if (leftAnswer?.id != null || rightAnswer?.id != null) {
                    if (leftAnswer?.id == null || rightAnswer?.id == null) return false
                    if (String(leftAnswer.id) !== String(rightAnswer.id)) return false
                    if (
                        !COMPACT_ANSWER_KEYS.every(key =>
                            stableCacheValuesEqual(leftAnswer[key], rightAnswer[key], {
                                entryKey: key,
                                leftStack,
                                rightStack,
                            }),
                        )
                    ) {
                        return false
                    }
                    continue
                }
                if (
                    !stableCacheValuesEqual(entry, other, {
                        leftStack,
                        rightStack,
                        isArrayElement: true,
                    })
                ) {
                    return false
                }
            }
            return true
        }
    }

    if (entryKey === 'parent' || entryKey === 'grand_parent') {
        const compactLeft = isComparableRecord(left)
        const compactRight = isComparableRecord(right)
        if (compactLeft || compactRight) {
            if (!compactLeft || !compactRight) return false
            return COMPACT_PARENT_KEYS.every(key =>
                stableCacheValuesEqual(left[key], right[key], {
                    entryKey: key,
                    leftStack,
                    rightStack,
                }),
            )
        }
    }

    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
            return false
        }
        if (leftStack.has(left) || rightStack.has(right)) return false
        leftStack.add(left)
        rightStack.add(right)
        let equal = true
        for (let index = 0; index < left.length; index += 1) {
            if (
                stableCacheValuesEqual(left[index], right[index], {
                    leftStack,
                    rightStack,
                    isArrayElement: true,
                })
            ) {
                continue
            }
            equal = false
            break
        }
        leftStack.delete(left)
        rightStack.delete(right)
        return equal
    }

    if (isComparableRecord(left) || isComparableRecord(right)) {
        if (!isComparableRecord(left) || !isComparableRecord(right)) return false
        if (leftStack.has(left) || rightStack.has(right)) return false
        leftStack.add(left)
        rightStack.add(right)

        const comparableKeys = (record: Record<string, unknown>) =>
            Object.keys(record).filter(key => {
                if (key === 'draft_answer_id') return false
                if (isTopLevel && ignoredTopLevelKeys?.has(key)) return false
                if (VOLATILE_CACHE_KEYS.has(key)) return true
                return !isJsonOmittedObjectValue(record[key])
            })
        const leftKeys = comparableKeys(left)
        const rightKeys = comparableKeys(right)
        const equal =
            leftKeys.length === rightKeys.length &&
            leftKeys.every(key => {
                if (!Object.prototype.hasOwnProperty.call(right, key)) return false
                if (VOLATILE_CACHE_KEYS.has(key)) return true
                return stableCacheValuesEqual(left[key], right[key], {
                    entryKey: key,
                    leftStack,
                    rightStack,
                })
            })

        leftStack.delete(left)
        rightStack.delete(right)
        return equal
    }

    return false
}

function cacheEntriesEqualIgnoringVolatileMediaTokens(
    left: Record<string, unknown> | null | undefined,
    right: Record<string, unknown> | null | undefined,
    ignoredTopLevelKeys: string[] = [],
): boolean {
    if (!left || !right) return false

    try {
        return stableCacheValuesEqual(left, right, {
            ignoredTopLevelKeys: new Set(ignoredTopLevelKeys),
            isTopLevel: true,
            leftStack: new Set(),
            rightStack: new Set(),
        })
    } catch {
        return false
    }
}

function isPrivateAnswerPlaceholderId(answerId: unknown): boolean {
    return String(answerId || '') === '__PRIVATE__'
}

function preserveAnswerFieldIfMissing(
    target: PartialAnswerCacheEntry,
    source: PartialAnswerCacheEntry | null | undefined,
    key: string,
    hasValue: (value: unknown) => boolean = value => value != null,
) {
    if (hasValue(target[key])) return
    if (!source || !hasValue(source[key])) return
    target[key] = cloneAnswerCacheField(source[key])
}

function mergedAnonymousShareLinkIds(
    incoming: PartialAnswerCacheEntry | null | undefined,
    existing: PartialAnswerCacheEntry | null | undefined,
) {
    const incomingIds = Array.isArray(incoming?.anonymous_share_link_ids)
        ? incoming.anonymous_share_link_ids.map(String).filter(Boolean)
        : []
    const existingIds = Array.isArray(existing?.anonymous_share_link_ids)
        ? existing.anonymous_share_link_ids.map(String).filter(Boolean)
        : []
    if (!incomingIds.length && !existingIds.length) return undefined
    return [...new Set([...existingIds, ...incomingIds])]
}

const STALE_ANSWER_RENDER_FIELDS = [
    'updated_at',
    'content',
    'display_content',
    'content_preview',
    'delta_json',
    'nested_delta_json',
    'content_translated',
    'detected_lang',
    'url',
    'url_id',
    'embeds',
    'child_quests',
    'images',
    'images_v2',
    'image_urls',
    'answer_image_url',
    'local_answer_image_url',
    'file_urls',
    'files',
    'recording_url',
    'mp4_recording_url',
    'recording_thumbnail_url',
    'recording_info',
    'recording_content_type',
    'original_recording_type',
    'action_requests',
    'ai_runs',
    'message_type',
    'poll_options_payload',
    'poll_open',
    'poll_results_visible',
    'poll_user_vote_option_id',
    'poll_total_votes',
    'poll_expires_at',
    'dice',
    'transcript',
    'image_generation_failed',
    'image_generation_failure_reason',
    'is_generating_image',
    'virtual_user_name',
    'virtual_user_icon_url',
    'deleted',
    'archive',
    'reply_to_answer_id',
] as const

function parseCacheTimestamp(value: unknown): number | null {
    if (value instanceof Date) {
        const timestamp = value.getTime()
        return Number.isFinite(timestamp) ? timestamp : null
    }

    if (typeof value !== 'string' && typeof value !== 'number') return null

    const parsed = new Date(value).getTime()
    return Number.isFinite(parsed) ? parsed : null
}

function answerPayloadIsOlderThanCached(
    incomingEntry: PartialAnswerCacheEntry,
    existingEntry: PartialAnswerCacheEntry | null | undefined,
): boolean {
    const incomingUpdatedAt = parseCacheTimestamp(incomingEntry.updated_at)
    const existingUpdatedAt = parseCacheTimestamp(existingEntry?.updated_at)
    if (incomingUpdatedAt == null || existingUpdatedAt == null) return false

    return incomingUpdatedAt < existingUpdatedAt
}

function preserveAnswerRenderFieldsFromNewerCache(
    target: PartialAnswerCacheEntry,
    source: PartialAnswerCacheEntry | null | undefined,
) {
    if (!source) return

    STALE_ANSWER_RENDER_FIELDS.forEach(key => {
        if (!hasOwnKey(source, key)) return

        const sourceValue = (source as Record<string, unknown>)[key]
        if (sourceValue === undefined) return
        ;(target as Record<string, unknown>)[key] = cloneAnswerCacheField(sourceValue)
    })
}

function aiRunOutputUrls(run: unknown): unknown[] {
    if (!run || typeof run !== 'object') return []
    const entry = run as Record<string, unknown>
    const outputUrls = entry.output_urls
    const outputUrlsCamel = entry.outputUrls

    if (Array.isArray(outputUrls) && outputUrls.length > 0) return outputUrls
    if (Array.isArray(outputUrlsCamel) && outputUrlsCamel.length > 0) return outputUrlsCamel
    return []
}

function aiRunHasOutputUrls(run: unknown): boolean {
    return aiRunOutputUrls(run).length > 0
}

function aiRunIdentityKeys(run: unknown, fallbackIndex: number): string[] {
    if (!run || typeof run !== 'object') return [`index:${fallbackIndex}`]
    const entry = run as Record<string, unknown>
    const keys = [
        entry.id != null ? `id:${String(entry.id)}` : null,
        entry.ai_run_id != null ? `id:${String(entry.ai_run_id)}` : null,
        entry.aiRunId != null ? `id:${String(entry.aiRunId)}` : null,
        `generation:${String(entry.action_index ?? entry.actionIndex ?? 0)}:${String(
            entry.generation_index ?? entry.generationIndex ?? 0,
        )}`,
        `index:${fallbackIndex}`,
    ]

    return keys.filter((key): key is string => Boolean(key))
}

function aiRunBlocksOutputPreservation(run: unknown): boolean {
    if (!run || typeof run !== 'object') return true
    const status = String((run as Record<string, unknown>).status || '').toLowerCase()
    return ['failed', 'canceled'].includes(status)
}

function aiRunCompleteStatus(run: unknown): string | null {
    if (!run || typeof run !== 'object') return null
    const status = String((run as Record<string, unknown>).status || '').toLowerCase()
    return status === 'succeeded' || status === 'finished' ? status : null
}

function aiRunStatusForPreservedOutputs(run: unknown): string {
    return aiRunCompleteStatus(run) || 'succeeded'
}

function mergeAiRunsPreservingCompletedOutputs<T>(
    incomingRuns: T[] | unknown,
    existingRuns: T[] | unknown,
): T[] {
    if (!hasArrayValues(existingRuns)) {
        return Array.isArray(incomingRuns) ? incomingRuns : []
    }
    if (!Array.isArray(incomingRuns) || incomingRuns.length === 0) {
        return cloneAnswerCacheField(existingRuns) as T[]
    }

    const existingByKey = new Map<string, T>()
    existingRuns.forEach((run, index) => {
        if (!aiRunHasOutputUrls(run)) return
        aiRunIdentityKeys(run, index).forEach(key => existingByKey.set(key, run as T))
    })
    if (existingByKey.size === 0) return incomingRuns as T[]

    let changed = false
    const mergedRuns = incomingRuns.map((run, index) => {
        if (!run || typeof run !== 'object') return run
        if (aiRunHasOutputUrls(run) || aiRunBlocksOutputPreservation(run)) return run

        const existingRun = aiRunIdentityKeys(run, index)
            .map(key => existingByKey.get(key))
            .find(Boolean)
        const outputUrls = aiRunOutputUrls(existingRun)
        if (!outputUrls.length) return run

        changed = true
        return {
            ...(run as Record<string, unknown>),
            output_urls: cloneAnswerCacheField(outputUrls),
            status: aiRunStatusForPreservedOutputs(existingRun),
        } as T
    })

    return changed ? mergedRuns : (incomingRuns as T[])
}

const TWITTER_STATUS_PENDING = 'twitter_pending'
const TWITTER_SERVICE_NAME = 'twitter'
const TWITTER_STATUS_REGEXP = /^https?:\/\/(?:www\.)?(?:x|twitter)\.com\/[^\/]+\/status\/([0-9]+)/i
const STICKY_URL_CACHE_FIELDS = [
    'address',
    'service_name',
    'service_status',
    'service_id',
    'tweet_id',
    'service_json',
    'service_username',
    'avatar_url',
    'image_urls',
    'image_metadata',
    'video_urls',
    'video_thumbnail_urls',
    'video_metadata',
    'embed',
    'preview_image_url',
    'preview_url',
    'selection',
    'title',
    'domain',
    'processed',
    'full_text_attachment_id',
]

function urlObject(value: unknown): PartialUrlCacheEntry | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null
    return value as PartialUrlCacheEntry
}

function getTweetIdFromUrlCacheEntry(url: PartialUrlCacheEntry | null | undefined) {
    const explicitId = url?.tweet_id || (url?.service_json as Record<string, unknown>)?.id
    if (explicitId) return String(explicitId)

    if (url?.service_name === TWITTER_SERVICE_NAME && url?.service_id) {
        return String(url.service_id)
    }

    const address = typeof url?.address === 'string' ? url.address : ''
    const match = address.match(TWITTER_STATUS_REGEXP)
    return match?.[1] || null
}

function urlCacheEntriesReferToSameResource(
    existingUrl: PartialUrlCacheEntry | null | undefined,
    incomingUrl: PartialUrlCacheEntry | null | undefined,
) {
    if (!existingUrl || !incomingUrl) return false
    if (existingUrl.id != null && incomingUrl.id != null && existingUrl.id === incomingUrl.id) {
        return true
    }
    if (existingUrl.address && incomingUrl.address && existingUrl.address === incomingUrl.address) {
        return true
    }

    const existingTweetId = getTweetIdFromUrlCacheEntry(existingUrl)
    const incomingTweetId = getTweetIdFromUrlCacheEntry(incomingUrl)
    return !!existingTweetId && existingTweetId === incomingTweetId
}

function urlCacheEntryHasSyncedTweetData(url: PartialUrlCacheEntry | null | undefined) {
    const serviceJson = url?.service_json as Record<string, unknown> | null | undefined
    return !!serviceJson?.id && !serviceJson?.error
}

function isBlankIncomingUrlValue(value: unknown, existingValue?: unknown) {
    if (value === undefined || value === null || value === '') return true
    if (
        Array.isArray(value) &&
        value.length === 0 &&
        Array.isArray(existingValue) &&
        existingValue.length > 0
    ) {
        return true
    }
    if (
        !Array.isArray(value) &&
        typeof value === 'object' &&
        Object.keys(value).length === 0 &&
        existingValue &&
        typeof existingValue === 'object'
    ) {
        return true
    }
    return false
}

function mergeCachedUrl(
    existingUrlValue: unknown,
    incomingUrlValue: unknown,
): PartialUrlCacheEntry | null {
    if (incomingUrlValue === null) return null

    const existingUrl = urlObject(existingUrlValue)
    const incomingUrl = urlObject(incomingUrlValue)
    if (!incomingUrl) return existingUrl ? { ...existingUrl } : null
    if (!existingUrl || !urlCacheEntriesReferToSameResource(existingUrl, incomingUrl)) {
        return { ...incomingUrl }
    }

    const merged: PartialUrlCacheEntry = { ...existingUrl, ...incomingUrl }
    STICKY_URL_CACHE_FIELDS.forEach(field => {
        const incomingValue = incomingUrl[field]
        const existingValue = existingUrl[field]
        if (
            field === 'service_status' &&
            urlCacheEntryHasSyncedTweetData(existingUrl) &&
            incomingValue === TWITTER_STATUS_PENDING
        ) {
            merged.service_status = existingValue as string | null
            return
        }
        if (
            field === 'service_json' &&
            urlCacheEntryHasSyncedTweetData(existingUrl) &&
            !urlCacheEntryHasSyncedTweetData({
                service_json: incomingValue as Record<string, unknown> | null,
            })
        ) {
            merged.service_json = existingValue as Record<string, unknown> | null
            return
        }
        if (
            isBlankIncomingUrlValue(incomingValue, existingValue) &&
            !isBlankIncomingUrlValue(existingValue)
        ) {
            merged[field] = cloneAnswerCacheField(existingValue)
        }
    })

    return merged
}

export function isHydratedAnswerCacheEntry(answer: unknown): boolean {
    const entry = answer as PartialAnswerCacheEntry | null | undefined
    if (!entry?.id) return false
    if (isAnswerStubCacheEntry(entry)) return false
    return answerCacheEntryHasRenderableData(entry)
}

export function isViewerHydratedAnswerCacheEntry(answer: unknown): boolean {
    if (!isHydratedAnswerCacheEntry(answer)) return false

    const entry = answer as PartialAnswerCacheEntry
    if (!answerEmbedsHavePermalinkTargets(entry)) return false

    const isModelMessage = entry.message_type === 'chatgpt' || entry.message_type === 'agent'
    if (!isModelMessage || !Array.isArray(entry.embeds)) return true

    return !entry.embeds.some(embed => {
        if (embed && typeof embed === 'object' && 'id' in embed) {
            return isPrivateAnswerPlaceholderId(embed.id)
        }
        return isPrivateAnswerPlaceholderId(embed)
    })
}

export function cacheAnswer(
    answer: AnswerOrDraft,
    update_timestamp: boolean = true,
    options: CacheAnswerOptions = {},
) {
    if (!answer) return null
    const answerId = normalizedEntityId(answer.id)
    if (!answerId) return null
    const { preservePendingSyntheticUpvalues = true, skipEntityRetentionPrune = false } = options
    let existingAnswerForStableCompare: Answer | null | undefined = null
    touchEntityCache('answer', answerId)

    // Normalize user into its own cache entry.
    // NOTE: we intentionally do NOT strip the embedded user object yet because many legacy
    // callsites read `answer.user.*` directly (without going through getCachedAnswer()).
    // We still cache the user separately so volatile fields (avatar, name) can be updated
    // without rewriting every cached answer.
    try {
        const u = 'user' in answer ? answer.user : undefined
        const uid = u?.id || ('user_id' in answer ? answer.user_id : undefined)
        if (uid) {
            // Embedded users on Answer payloads are not authoritative (answers can be server-cached),
            // so never allow them to clobber fresher userNorm data (name/avatar URLs).
            if (u && typeof u === 'object') cacheUser(u, false)
        }
    } catch (e) {}

    // Work with a mutable view so we can patch local image URLs without casting.
    const mutable = answer as AnswerOrDraft & {
        local_answer_image_url?: string
        answer_image_url?: string | null
        recording_url?: string | null
        mp4_recording_url?: string | null
        images_v2?: unknown[]
        image_urls?: unknown[]
        file_urls?: unknown[]
        action_requests?: unknown[]
        image_generation_failed?: boolean
        message_type?: string | null
        poll_open?: boolean
        poll_results_visible?: boolean
        poll_user_vote_option_id?: string | null
        poll_options_payload?: Array<{
            id: string
            label: string
            position: number
            votes_count?: number
            percentage?: number
        }>
    }

    // Preserve local, client-only media URLs across server/socket updates.
    // These are used to avoid transient "missing image" states right after upload.
    try {
        const existing = peekQueryData<Answer>(['answer', answerId])
        existingAnswerForStableCompare = existing
        const existingEntry = existing as unknown as PartialAnswerCacheEntry | null | undefined
        const mutableEntry = mutable as unknown as PartialAnswerCacheEntry
        // Server payloads omit this client-only link. Keep an active edit (and its
        // uploads) attached to the post when a refresh arrives.
        if (!hasOwnKey(mutableEntry, 'draft_answer_id') && existing?.draft_answer_id) {
            ;(mutable as Answer).draft_answer_id = existing.draft_answer_id
        }
        const mutableWithUrl = mutable as unknown as { url?: PartialUrlCacheEntry | null }
        const existingIsHydrated = isHydratedAnswerCacheEntry(existingEntry)
        const incomingIsHydrated = isHydratedAnswerCacheEntry(mutableEntry)
        const existingLocalImg = existing?.local_answer_image_url
        const existingVisibleImg = existing?.answer_image_url
        const incomingIsOpenPoll = mutable.message_type === 'poll' && mutable.poll_open !== false
        const existingPollSelection = existing?.poll_user_vote_option_id ?? null
        const existingPollResultsVisible = existing?.poll_results_visible === true
        const existingPollOptions = Array.isArray(existing?.poll_options_payload)
            ? existing.poll_options_payload
            : []
        const incomingHasActionRequests =
            Array.isArray(mutable.action_requests) && mutable.action_requests.length > 0
        const preservePrivateAiActionMetadata = shouldPreservePrivateAiActionMetadata(
            mutableEntry,
            existingEntry,
            typeof gon !== 'undefined' ? gon?.currentUser?.id : null,
        )
        const explicitlyClearingLocalImg =
            'local_answer_image_url' in mutable && !mutable.local_answer_image_url
        const explicitlyClearingVisibleImg =
            'answer_image_url' in mutable && !mutable.answer_image_url

        const isLocalUrl = (u: unknown): u is string =>
            typeof u === 'string' && (u.startsWith('blob:') || u.startsWith('data:'))

        // Draft quest updates (such as changing the destination stream) can carry the parent
        // object from before image selection. Keep the plural, client-only image state unless
        // the update explicitly replaces or clears it. Copy only the arrays so File instances
        // remain intact for submission.
        if (existingEntry?.is_draft === true && mutableEntry.is_draft !== false) {
            if (
                (!hasOwnKey(mutableEntry, 'pending_images') ||
                    mutableEntry.pending_images === undefined) &&
                existingEntry.pending_images?.length
            ) {
                mutableEntry.pending_images = [...existingEntry.pending_images]
            }
            if (
                (!hasOwnKey(mutableEntry, 'local_image_urls') ||
                    mutableEntry.local_image_urls === undefined) &&
                existingEntry.local_image_urls?.length
            ) {
                mutableEntry.local_image_urls = [...existingEntry.local_image_urls]
            }
        }

        if ('url' in mutableWithUrl) {
            mutableWithUrl.url = mergeCachedUrl(existing?.url, mutableWithUrl.url)
        }

        // Embeds are an independent relationship group. Lean quest/socket payloads that omit
        // the group must not clear an optimistic or already-hydrated relationship. An explicit
        // `embeds: []` remains authoritative and can still clear the relationship.
        if (!hasOwnKey(mutableEntry, 'embeds') || mutableEntry.embeds === undefined) {
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'embeds', hasArrayValues)
        }

        // The reply edge (bottom_editor): a lean payload OMITTING the key must
        // not wipe it (the child_answer_count failure mode — the sent quote
        // line would flicker off on stream/socket overwrites), but an EXPLICIT
        // null stays authoritative: the server drops invalid targets at create
        // and clears the edge on cross-quest moves, and the optimistic draft's
        // value must not resurrect a rejected edge.
        if (!hasOwnKey(mutableEntry, 'reply_to_answer_id')) {
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'reply_to_answer_id')
        }

        const anonymousShareLinkIds = mergedAnonymousShareLinkIds(mutableEntry, existingEntry)
        if (anonymousShareLinkIds) {
            mutableEntry.anonymous_share_link_ids = anonymousShareLinkIds
        }

        if (existingIsHydrated && !incomingIsHydrated) {
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'delta_json', hasObjectValues)
            preserveAnswerFieldIfMissing(
                mutableEntry,
                existingEntry,
                'nested_delta_json',
                hasObjectValues,
            )
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'url', hasObjectValues)
            preserveAnswerFieldIfMissing(
                mutableEntry,
                existingEntry,
                'user',
                value => !!value && typeof value === 'object' && Object.keys(value).length > 0,
            )
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'embeds', hasArrayValues)
            preserveAnswerFieldIfMissing(
                mutableEntry,
                existingEntry,
                'child_quests',
                hasArrayValues,
            )
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'images_v2', hasArrayValues)
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'image_urls', hasArrayValues)
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'file_urls', hasArrayValues)
            preserveAnswerFieldIfMissing(
                mutableEntry,
                existingEntry,
                'poll_options_payload',
                hasArrayValues,
            )
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'answer_image_url')
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'recording_url')
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'mp4_recording_url')
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'recording_thumbnail_url')
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'virtual_user_name')
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'virtual_user_icon_url')
        }
        if (answerPayloadIsOlderThanCached(mutableEntry, existingEntry)) {
            preserveAnswerRenderFieldsFromNewerCache(mutableEntry, existingEntry)
        }
        if (incomingHasActionRequests) {
            preserveAnswerFieldIfMissing(mutableEntry, existingEntry, 'ai_runs', hasArrayValues)
        }
        if (hasArrayValues(existingEntry?.ai_runs)) {
            mutableEntry.ai_runs = mergeAiRunsPreservingCompletedOutputs(
                mutableEntry.ai_runs,
                existingEntry?.ai_runs,
            )
            mutableEntry.ai_runs = mergeAiRunsPreservingPrivatePayloads(
                mutableEntry.ai_runs,
                existingEntry?.ai_runs,
                preservePrivateAiActionMetadata,
            )
        }
        if (
            hasArrayValues(mutableEntry.action_requests) &&
            hasArrayValues(existingEntry?.action_requests)
        ) {
            mutableEntry.action_requests = mergeActionRequestsPreservingPrivateMetadata(
                mutableEntry.action_requests,
                existingEntry?.action_requests,
                preservePrivateAiActionMetadata,
            )
        }
        if (preservePendingSyntheticUpvalues) {
            preserveUnresolvedSyntheticUpvalues(mutableEntry, existingEntry)
        }

        // If we already have a local image URL, keep it unless the incoming answer explicitly provides one.
        if (
            !('local_answer_image_url' in mutable) &&
            !explicitlyClearingLocalImg &&
            isLocalUrl(existingLocalImg)
        ) {
            mutable.local_answer_image_url = existingLocalImg
        }

        // If the incoming payload blanks answer_image_url but we have a local one, keep rendering via the local URL.
        if (
            !explicitlyClearingLocalImg &&
            !explicitlyClearingVisibleImg &&
            (!mutable.answer_image_url || mutable.answer_image_url === null) &&
            isLocalUrl(existingVisibleImg)
        ) {
            mutable.answer_image_url = existingVisibleImg
        } else if (
            !explicitlyClearingLocalImg &&
            !explicitlyClearingVisibleImg &&
            (!mutable.answer_image_url || mutable.answer_image_url === null) &&
            isLocalUrl(mutable.local_answer_image_url)
        ) {
            mutable.answer_image_url = mutable.local_answer_image_url
        }

        // Poll websocket refreshes intentionally use a viewer-neutral payload.
        // Preserve the current viewer's vote/result state while still accepting
        // updated aggregate poll totals from the incoming answer.
        if (incomingIsOpenPoll) {
            if (mutable.poll_user_vote_option_id == null && existingPollSelection) {
                mutable.poll_user_vote_option_id = existingPollSelection
            }

            if (
                mutable.poll_results_visible === false &&
                (existingPollResultsVisible || existingPollSelection)
            ) {
                mutable.poll_results_visible = true
            }

            if (
                Array.isArray(mutable.poll_options_payload) &&
                (existingPollResultsVisible || existingPollSelection)
            ) {
                const existingPollOptionsById = new Map(
                    existingPollOptions.map(option => [String(option.id), option]),
                )

                mutable.poll_options_payload = mutable.poll_options_payload.map(option => {
                    const existingOption = existingPollOptionsById.get(String(option.id))
                    if (!existingOption) return option

                    const incomingHasStats = option.votes_count != null || option.percentage != null
                    if (incomingHasStats) return option

                    return {
                        ...option,
                        votes_count: existingOption.votes_count,
                        percentage: existingOption.percentage,
                    }
                })
            }
        }
    } catch (e) {}

    // Cache embeds
    answer.embeds?.forEach(embed => {
        const embedGeometryProjection = primeAnswerGeometryProjection(embed)
        adoptEmbeddedAnswerGeometryProjection(embed, embedGeometryProjection)

        const embedId = normalizedEntityId(embed?.id)
        const cachedEmbed = embedId
            ? (peekQueryData(['answer', embedId]) as PartialAnswerCacheEntry | null)
            : null
        const incomingEmbedIsHydrated = isHydratedAnswerCacheEntry(embed)
        const cachedEmbedIsHydrated = isHydratedAnswerCacheEntry(cachedEmbed)
        const incomingEmbedHasTarget = answerEmbedsHavePermalinkTargets({ embeds: [embed] })
        const cachedEmbedHasTarget = answerEmbedsHavePermalinkTargets({ embeds: [cachedEmbed] })
        const incomingEmbedRuns = Array.isArray(embed?.ai_runs) ? embed.ai_runs : []
        const incomingEmbedHasCompletedOutputs = incomingEmbedRuns.some(aiRunHasOutputUrls)
        const cachedEmbedRuns = cachedEmbed?.ai_runs
        const incomingEmbedIsNewer =
            !!cachedEmbed && answerPayloadIsOlderThanCached(cachedEmbed, embed)
        const incomingEmbedAddsCompletedOutputs =
            incomingEmbedHasCompletedOutputs &&
            (incomingEmbedIsNewer ||
                mergeAiRunsPreservingCompletedOutputs(cachedEmbedRuns, incomingEmbedRuns) !==
                    cachedEmbedRuns)
        const mergedShareLinkIds = mergedAnonymousShareLinkIds(embed, cachedEmbed)
        const incomingEmbedAddsShareLinkIds =
            !!mergedShareLinkIds &&
            mergedShareLinkIds.length >
                (Array.isArray(cachedEmbed?.anonymous_share_link_ids)
                    ? cachedEmbed.anonymous_share_link_ids.length
                    : 0)

        if (
            embed &&
            embedId &&
            (!cachedEmbed ||
                (incomingEmbedIsHydrated && !cachedEmbedIsHydrated) ||
                (incomingEmbedHasTarget && !cachedEmbedHasTarget) ||
                incomingEmbedAddsShareLinkIds ||
                incomingEmbedAddsCompletedOutputs)
        ) {
            let nextEmbed =
                incomingEmbedIsHydrated && !cachedEmbedIsHydrated && cachedEmbed
                    ? markAnswerStubState({ ...cachedEmbed, ...embed })
                    : embed
            if (cachedEmbed && incomingEmbedAddsShareLinkIds) {
                nextEmbed = markAnswerStubState({ ...cachedEmbed, ...nextEmbed })
            }
            if (cachedEmbed && incomingEmbedAddsCompletedOutputs) {
                const preservePrivateAiActionMetadata = shouldPreservePrivateAiActionMetadata(
                    embed,
                    cachedEmbed,
                    typeof gon !== 'undefined' ? gon?.currentUser?.id : null,
                )
                nextEmbed = markAnswerStubState({
                    ...cachedEmbed,
                    ...embed,
                    ai_runs: mergeAiRunsPreservingPrivatePayloads(
                        mergeAiRunsPreservingCompletedOutputs(incomingEmbedRuns, cachedEmbedRuns),
                        cachedEmbedRuns,
                        preservePrivateAiActionMetadata,
                    ),
                })
            }
            if (mergedShareLinkIds) {
                nextEmbed = markAnswerStubState({
                    ...nextEmbed,
                    anonymous_share_link_ids: mergedShareLinkIds,
                })
            }
            if (nextEmbed !== embed) {
                adoptAnswerGeometryProjection(nextEmbed, embedGeometryProjection)
                adoptEmbeddedAnswerGeometryProjection(nextEmbed, embedGeometryProjection)
            }
            set(['answer', embedId], nextEmbed)
        }
    })

    // Cache child quests
    answer.child_quests?.forEach(quest => {
        const questId = normalizedEntityId(quest?.id)
        if (quest && questId && !peekQueryData(['quest', questId])) {
            set(['quest', questId], quest)
        }
    })

    const nextCachedAnswer = markAnswerStubState({
        ...answer,
        ...(answer.delta_json && {
            delta_json: {
                ...answer.delta_json,
                ops: answer.delta_json.ops ? [...answer.delta_json.ops] : [],
            },
        }),
        ...(answer.embeds && {
            embeds: answer.embeds
                .filter(embed => embed && embed.id)
                .map(embed => ({ id: embed.id })),
        }),
        ...('draft_answer' in answer &&
            (answer as Answer).draft_answer?.id && {
                draft_answer_id: (answer as Answer).draft_answer.id,
            }),
        ...(update_timestamp &&
            !answer.updated_at && {
                // Local/cache-only answers need a timestamp so memoized answer rows update,
                // but server answers must keep their canonical updated_at to avoid refresh churn.
                updated_at: new Date(),
            }),
        // Preserve child_quests if present
        ...(answer.child_quests && { child_quests: answer.child_quests }),
    })

    // Project from the full normalized payload before embeds are reduced to id stubs, then
    // attach that immutable projection to the exact object entering the entity cache.
    const geometryProjection = primeAnswerGeometryProjection({
        ...nextCachedAnswer,
        embeds: answer.embeds,
    })
    adoptAnswerGeometryProjection(nextCachedAnswer, geometryProjection)
    adoptEmbeddedAnswerGeometryProjection(nextCachedAnswer, geometryProjection)

    // Retain the height-estimation fingerprint (dims, text length, url kind, ...) so that
    // when this entry is later evicted and a row remounts against a bare placeholder, the
    // height estimators can still compute the exact reserved height (no re-measure jump).
    // `answer` here is the merged, fullest view (embeds not yet stripped to id stubs).
    rememberAnswerHeightFingerprint(answerId, answer)

    // Quest refreshes can regenerate visibility tokens for the same media. Treat those as
    // transport metadata, not render data, so title answers do not rerender/flicker on every
    // pull-to-refresh when the actual answer content is unchanged.
    const existingGeometryProjection = primeAnswerGeometryProjection(existingAnswerForStableCompare)
    if (
        existingAnswerForStableCompare?.draft_answer_id ===
            (nextCachedAnswer as Answer).draft_answer_id &&
        cacheEntriesEqualIgnoringVolatileMediaTokens(
            existingAnswerForStableCompare as unknown as PartialAnswerCacheEntry,
            nextCachedAnswer as unknown as PartialAnswerCacheEntry,
        ) &&
        cacheEntriesEqualIgnoringVolatileMediaTokens(existingGeometryProjection, geometryProjection)
    ) {
        return existingAnswerForStableCompare || answer
    }

    // Cache the answer
    set(['answer', answerId], nextCachedAnswer, {
        skipEntityRetentionPrune,
    })
    return answer
}

function normalizeQuestHydratedFields(
    quest: QuestWithAncestors | null | undefined,
    fallbackQuest: QuestWithAncestors | null | undefined = null,
    visited: Set<string> = new Set(),
    deps: EntityCacheIdSets = createEntityCacheIdSets(),
    hydrateSortedAnswers = true,
): QuestWithAncestors | null {
    return normalizeQuestAncestors({
        quest,
        fallbackQuest,
        getAnswerById: answerId => getCachedAnswer(answerId, new Set(visited), deps),
        hydrateSortedAnswers,
    }) as QuestWithAncestors | null
}

export function getCachedQuest(
    questId: string,
    visited: Set<string> = new Set(),
    deps: EntityCacheIdSets = createEntityCacheIdSets(),
): Quest {
    const normalizedQuestId = normalizedEntityId(questId)
    if (!normalizedQuestId || visited.has(`quest:${normalizedQuestId}`)) return null
    const canUseReadCache = visited.size === 0

    visited.add(`quest:${normalizedQuestId}`)

    addNormalizedReadDependency(deps, 'quest', normalizedQuestId)
    const quest = peekQueryData(['quest', normalizedQuestId]) as QuestWithAncestors | null
    if (!quest) return null
    if (
        compactThreadQuestRefCounts.has(normalizedQuestId) ||
        (quest as any).__knovCompactThreadAnswers
    ) {
        return normalizeQuestHydratedFields(quest, null, visited, deps, false) as Quest
    }
    if (canUseReadCache) {
        const cached = normalizedQuestReadCache.get(normalizedQuestId)
        if (cached?.source === quest) return cached.value as Quest
    }

    const normalizedQuest = normalizeQuestHydratedFields(quest, null, visited, deps)
    if (canUseReadCache && normalizedQuest) {
        setNormalizedQuestReadCache(normalizedQuestId, quest, normalizedQuest, deps)
    }
    return normalizedQuest as Quest
}

export function getCachedThreadQuest(questId: string): Quest {
    const normalizedQuestId = normalizedEntityId(questId)
    if (!normalizedQuestId) return null
    const quest = peekQueryData(['quest', normalizedQuestId]) as QuestWithAncestors | null
    if (!quest) return null

    return normalizeQuestHydratedFields(
        quest,
        null,
        new Set([`quest:${normalizedQuestId}`]),
        createEntityCacheIdSets(),
        false,
    ) as Quest
}

export function getCachedStreamCardQuest(
    questId: string,
    previewAnswerIds: Array<string | number> = [],
): Quest {
    const normalizedQuestId = normalizedEntityId(questId)
    if (!normalizedQuestId) return null
    const quest = peekQueryData(['quest', normalizedQuestId]) as QuestWithAncestors | null
    if (!quest) return null

    // Active natural threads compact their query transport while retaining hydrated
    // Answer entities. A stream card returning from that thread needs the projected
    // Answers immediately instead of waiting for another stream-preview request.
    const sortedAnswerIds = Array.isArray(quest.sorted_answer_ids)
        ? quest.sorted_answer_ids.map(String)
        : []
    const sortedAnswerIdSet = new Set(sortedAnswerIds)
    const requestedPreviewAnswerIds = Array.from(
        new Set(previewAnswerIds.filter(answerId => answerId != null).map(String)),
    ).filter(answerId => sortedAnswerIdSet.has(answerId))
    const normalizedPreviewAnswerIds =
        requestedPreviewAnswerIds.length > 0 ? requestedPreviewAnswerIds : sortedAnswerIds.slice(-3)
    const streamCardQuest =
        normalizedPreviewAnswerIds.length > 0
            ? {
                  ...quest,
                  __knovAnswerHydrationScope: 'stream-preview' as const,
                  __knovStreamPreviewAnswerIds: normalizedPreviewAnswerIds,
              }
            : quest

    return normalizeQuestHydratedFields(
        streamCardQuest,
        null,
        new Set([`quest:${normalizedQuestId}`]),
        createEntityCacheIdSets(),
        true,
    ) as Quest
}

export function retainCompactThreadQuestTransport(questId: string) {
    const normalizedQuestId = normalizedEntityId(questId)
    if (!normalizedQuestId) return () => undefined

    compactThreadQuestRefCounts.set(
        normalizedQuestId,
        (compactThreadQuestRefCounts.get(normalizedQuestId) || 0) + 1,
    )
    queryClient.setQueryData(['quest', normalizedQuestId], (quest: QuestWithAncestors | null) => {
        if (!quest) return quest
        return compactThreadQuestTransport(quest)
    })
    invalidateNormalizedReadCachesForEntity('quest', normalizedQuestId)

    return () => {
        const nextCount = (compactThreadQuestRefCounts.get(normalizedQuestId) || 1) - 1
        if (nextCount > 0) compactThreadQuestRefCounts.set(normalizedQuestId, nextCount)
        else {
            compactThreadQuestRefCounts.delete(normalizedQuestId)
            queryClient.setQueryData(
                ['quest', normalizedQuestId],
                (quest: (QuestWithAncestors & { __knovCompactThreadAnswers?: boolean }) | null) => {
                    if (!quest?.__knovCompactThreadAnswers) return quest
                    return clearCompactThreadQuestTransportMarker(quest)
                },
            )
            invalidateNormalizedReadCachesForEntity('quest', normalizedQuestId)
        }
    }
}

export function cacheQuests(quests) {
    return quests.map(quest => cacheQuest(quest))
}

export function mergeQuestWithLocalAnswersById(
    quest: Quest,
    preserveAnswerIds: Array<string | number> = [],
): Quest {
    if (!quest?.id) return quest

    const localQuest = getCachedQuest(String(quest.id), new Set())
    if (!localQuest) return quest

    const serverAnswers = Array.isArray(quest.sorted_answers)
        ? quest.sorted_answers.filter(Boolean)
        : []
    const localAnswers = Array.isArray(localQuest.sorted_answers)
        ? localQuest.sorted_answers.filter(Boolean)
        : []

    const serverIds = new Set(
        [
            ...(Array.isArray(quest.sorted_answer_ids) ? quest.sorted_answer_ids : []),
            ...serverAnswers.map(answer => answer?.id),
        ]
            .filter(Boolean)
            .map(id => String(id)),
    )
    const inferredOptimisticLocalIds = localAnswers
        .filter(answer => answer?.id != null)
        .filter(answer => !serverIds.has(String(answer.id)))
        .filter(answer => answer?.isLocal === true)
        .map(answer => String(answer.id))
    const preserveSet = new Set([
        ...preserveAnswerIds.map(id => String(id)),
        ...inferredOptimisticLocalIds,
    ])
    if (!preserveSet.size) return quest

    const localById = new Map(
        localAnswers
            .filter(answer => answer?.id != null)
            .map(answer => [String(answer.id), answer]),
    )

    const appendedAnswers = Array.from(preserveSet)
        .filter(id => !serverIds.has(id))
        .map(id => localById.get(id))
        .filter(Boolean)

    if (!appendedAnswers.length) return quest

    const baseIds = Array.isArray(quest.sorted_answer_ids)
        ? quest.sorted_answer_ids.map(id => String(id))
        : serverAnswers.map(answer => String(answer?.id)).filter(Boolean)
    const mergedIds = [...baseIds]
    appendedAnswers.forEach(answer => {
        const id = String(answer?.id)
        if (!mergedIds.includes(id)) mergedIds.push(id)
    })

    return {
        ...quest,
        sorted_answers: [...serverAnswers, ...appendedAnswers],
        sorted_answer_ids: mergedIds,
    }
}

function preserveExistingAnswerListForStaleQuest(
    incomingQuest: QuestWithAncestors,
    existingQuest: QuestWithAncestors | null,
): QuestWithAncestors {
    if (!existingQuest) return incomingQuest

    const incomingUpdatedAt = parseCacheTimestamp(incomingQuest.updated_at)
    const existingUpdatedAt = parseCacheTimestamp(existingQuest.updated_at)
    if (incomingUpdatedAt == null || existingUpdatedAt == null) return incomingQuest
    if (incomingUpdatedAt >= existingUpdatedAt) return incomingQuest
    if (!Array.isArray(existingQuest.sorted_answer_ids)) return incomingQuest

    return {
        ...incomingQuest,
        sorted_answer_ids: existingQuest.sorted_answer_ids,
        sorted_answers: existingQuest.sorted_answers,
    }
}

function preserveActiveFullThreadHydration(
    incomingQuest: QuestWithAncestors,
    existingQuest: QuestWithAncestors | null,
    activeThread: boolean,
): QuestWithAncestors {
    if (
        !activeThread ||
        incomingQuest.__knovAnswerHydrationScope !== 'stream-preview' ||
        existingQuest?.__knovAnswerHydrationScope !== 'full'
    )
        return incomingQuest

    return {
        ...incomingQuest,
        __knovAnswerHydrationScope: 'full',
        __knovStreamPreviewAnswerIds: incomingQuest.__knovStreamPreviewAnswerIds,
        // The incoming ordered ids remain authoritative. Normalization resolves retained
        // full entities, the newly fetched preview entities, and placeholders for any new
        // non-preview ids until the active thread hydration runway fetches them.
        sorted_answers: [
            ...(Array.isArray(existingQuest.sorted_answers) ? existingQuest.sorted_answers : []),
            ...(Array.isArray(incomingQuest.sorted_answers) ? incomingQuest.sorted_answers : []),
        ],
    }
}

function mergeQuestWithPendingOutboxAnswers(
    quest: QuestWithAncestors,
    pendingAnswers = getPendingAnswerOutboxAnswersForQuest(quest.id).filter(Boolean),
): QuestWithAncestors {
    if (!pendingAnswers.length) return quest

    const serverIds = new Set(
        [
            ...(Array.isArray(quest.sorted_answer_ids) ? quest.sorted_answer_ids : []),
            ...(Array.isArray(quest.sorted_answers)
                ? quest.sorted_answers.map(answer => answer?.id)
                : []),
        ]
            .filter(Boolean)
            .map(String),
    )
    const answersToAppend = pendingAnswers.filter(answer => !serverIds.has(String(answer?.id)))
    if (!answersToAppend.length) return quest

    answersToAppend.forEach(answer => cacheAnswer(answer as AnswerOrDraft))
    const baseAnswers = Array.isArray(quest.sorted_answers)
        ? quest.sorted_answers.filter(Boolean)
        : []
    const baseIds = Array.isArray(quest.sorted_answer_ids)
        ? quest.sorted_answer_ids.map(String)
        : baseAnswers.map(answer => String(answer?.id)).filter(Boolean)

    return {
        ...quest,
        sorted_answers: [...baseAnswers, ...(answersToAppend as Answer[])],
        sorted_answer_ids: [
            ...baseIds,
            ...answersToAppend
                .map(answer => String(answer?.id))
                .filter(id => !baseIds.includes(id)),
        ],
    }
}

export function cacheQuest(quest: Quest, options: CacheQuestOptions = {}) {
    const {
        compactThreadAnswers = false,
        skipParent = false,
        source = 'unknown',
        preserveAnswerIds = [],
        preserveViewContextFields = false,
        skipStreamQueryTouch = false,
    } = options
    if (!quest) return null
    const rawIncomingQuest = quest as QuestWithAncestors
    const incomingQuestId = normalizedEntityId(rawIncomingQuest?.id)
    if (!incomingQuestId) return null
    const shouldCompactThreadAnswers =
        compactThreadAnswers || compactThreadQuestRefCounts.has(incomingQuestId)
    const hasOwn = (obj: unknown, key: string) =>
        !!obj && Object.prototype.hasOwnProperty.call(obj, key)
    const existingQuest = peekQueryData(['quest', incomingQuestId]) as QuestWithAncestors | null
    const viewContextSafeIncomingQuest = preserveViewContextFields
        ? preserveQuestViewContextFields(rawIncomingQuest, existingQuest)
        : rawIncomingQuest
    const incomingQuest = preserveActiveFullThreadHydration(
        viewContextSafeIncomingQuest,
        existingQuest,
        shouldCompactThreadAnswers,
    )

    removePendingAnswerOutboxConfirmedByQuest(incomingQuest)
    const pendingOutboxAnswers = getPendingAnswerOutboxAnswersForQuest(incomingQuest.id).filter(
        Boolean,
    )

    // Normalize answers into their own cache entries before the quest fast path.
    // Quest equality intentionally treats sorted_answers as a compact list signature;
    // answer content changes are handled by the normalized answer cache.
    if (!skipParent && incomingQuest.parent) {
        cacheAnswer(incomingQuest.parent, true, { skipEntityRetentionPrune: true })
    }

    if (incomingQuest?.sorted_answers) {
        incomingQuest.sorted_answers
            // Compact thread entries carry ordering metadata only. Feeding one back through
            // an optimistic Quest update must not replace the hydrated Answer entity.
            .filter(answer => answer != null && !(answer as any)._thread_transport_stub)
            .forEach(answer => cacheAnswer(answer, true, { skipEntityRetentionPrune: true }))
    }

    if (
        existingQuest &&
        !shouldCompactThreadAnswers &&
        preserveAnswerIds.length === 0 &&
        pendingOutboxAnswers.length === 0 &&
        cacheEntriesEqualIgnoringVolatileMediaTokens(
            existingQuest as unknown as Record<string, unknown>,
            incomingQuest as unknown as Record<string, unknown>,
            [
                'updated_at',
                'last_action_created_at',
                ...['parent', 'grand_parent', 'parent_quest_id', 'team', 'ancestors'].filter(
                    key => !hasOwn(incomingQuest, key),
                ),
            ],
        )
    ) {
        return existingQuest
    }

    // NOTE: Draft preservation logic was removed. Drafts now live in draftAnswerStore,
    // completely separate from the quest cache. Socket updates can freely overwrite
    // cached quests without worrying about clobbering drafts.

    let finalQuest: QuestWithAncestors | null = null

    queryClient.setQueryData(['quest', incomingQuestId], oldData => {
        const existingQuest = (oldData || null) as QuestWithAncestors | null

        let partialMissing: string[] = []
        if (existingQuest) {
            if (existingQuest.parent_id && !hasOwn(incomingQuest, 'parent_id')) {
                partialMissing.push('parent_id')
            }
            if (existingQuest.grand_parent_id && !hasOwn(incomingQuest, 'grand_parent_id')) {
                partialMissing.push('grand_parent_id')
            }
            if (existingQuest.grand_parent?.id && !hasOwn(incomingQuest, 'grand_parent')) {
                partialMissing.push('grand_parent')
            }
            if (partialMissing.length && typeof window !== 'undefined' && (window as any).log) {
                console.warn('[cacheQuest] partial quest update; preserving cached parent chain', {
                    questId: incomingQuest.id,
                    partialMissing,
                })
            }
        }

        const pendingOutboxSafeIncomingQuest = mergeQuestWithPendingOutboxAnswers(
            incomingQuest,
            pendingOutboxAnswers,
        )
        const localAnswerSafeIncomingQuest = mergeQuestWithLocalAnswersById(
            pendingOutboxSafeIncomingQuest,
            preserveAnswerIds,
        ) as QuestWithAncestors
        const answerListSafeIncomingQuest = preserveExistingAnswerListForStaleQuest(
            localAnswerSafeIncomingQuest,
            existingQuest,
        )
        const mergedQuest = {
            ...(existingQuest || ({} as Quest)),
            ...answerListSafeIncomingQuest,
        } as QuestWithAncestors

        const teamId = mergedQuest.team_id ? String(mergedQuest.team_id) : null
        const team = mergedQuest.team as { id?: string | number } | null | undefined
        if (!teamId) {
            mergedQuest.team = null
        } else if (team?.id != null && String(team.id) !== teamId) {
            mergedQuest.team = null
        }

        const normalizedQuest = normalizeQuestHydratedFields(mergedQuest, existingQuest)
        if (!normalizedQuest) return oldData

        const expectedAnswerIds =
            normalizedQuest.__knovAnswerHydrationScope === 'stream-preview' &&
            Array.isArray(normalizedQuest.__knovStreamPreviewAnswerIds)
                ? normalizedQuest.__knovStreamPreviewAnswerIds
                : normalizedQuest.sorted_answer_ids
        const expectedCount = Array.isArray(expectedAnswerIds) ? expectedAnswerIds.length : 0
        const hydratedCount = Array.isArray(normalizedQuest.sorted_answers)
            ? normalizedQuest.sorted_answers.filter(Boolean).length
            : 0
        const hasMissingParent = !!normalizedQuest.parent_id && !normalizedQuest.parent
        const isIntentionalLocalSideQuest = !!normalizedQuest.isLocalChildQuest && hasMissingParent

        if (!isIntentionalLocalSideQuest && (hasMissingParent || hydratedCount < expectedCount)) {
            reportQuestCacheAnomaly('hydration_gap', {
                source,
                questId: normalizedQuest.id,
                expectedCount,
                hydratedCount,
                hasMissingParent,
                incomingHasParent: hasOwn(incomingQuest, 'parent'),
                incomingHasParentId: hasOwn(incomingQuest, 'parent_id'),
                existingHadParent: !!existingQuest?.parent,
                existingHadParentId: !!existingQuest?.parent_id,
                partialMissing,
            })
        }

        // A quest refetch often returns a fresh object graph even when nothing user-visible
        // changed except regenerated media visibility tokens. Keep the existing quest identity
        // in that case so mounted panels and answer lists do not churn during refresh.
        const storedQuest = shouldCompactThreadAnswers
            ? compactThreadQuestTransport(normalizedQuest)
            : clearCompactThreadQuestTransportMarker(normalizedQuest)

        if (
            cacheEntriesEqualIgnoringVolatileMediaTokens(
                existingQuest as unknown as Record<string, unknown>,
                storedQuest as unknown as Record<string, unknown>,
                ['updated_at', 'last_action_created_at', 'parent', 'grand_parent'],
            )
        ) {
            finalQuest = existingQuest
            return oldData
        }

        finalQuest = storedQuest
        return { ...storedQuest }
    })

    if (!finalQuest) return null
    // A newly created draft is cached immediately before its panel subscribes to it. Preserve it
    // through that short gap so any concurrent retention pass cannot evict the client-only id.
    // Once this grace expires, the mounted query observer protects active drafts as usual and
    // abandoned drafts remain eligible for normal cache pruning.
    if (finalQuest.is_draft === true) {
        entityCacheRetainedUntil.quest.set(
            String(finalQuest.id),
            Date.now() + LOCAL_DRAFT_CACHE_GRACE_MS,
        )
    }
    recordEntityCacheWrite('quest', finalQuest.id)
    invalidateNormalizedReadCachesForEntity('quest', finalQuest.id)
    if (!skipStreamQueryTouch) touchQuestInStreamQueries(finalQuest.id)
    return finalQuest
}

export function removeFromStream(filterKey, questId) {
    queryClient.setQueryData(['quests', filterKey], (oldData: PaginatedQuestsData | undefined) => {
        if (!oldData) return oldData

        return {
            ...oldData,
            pages: oldData.pages?.map(page => ({
                ...page,
                quests: page.quests?.filter(quest => quest.id !== questId),
            })),
        }
    })
}

export function updateCachedAnswer(answerId, params) {
    let answer = getCachedAnswer(answerId, new Set())
    if (!answer) return null

    // Create a new object that preserves delta_json if it's not being updated
    const updatedAnswer = {
        ...answer,
        ...params,
        ...(answer.delta_json &&
            !params.delta_json && {
                delta_json: {
                    ...answer.delta_json,
                    ops: answer.delta_json.ops ? [...answer.delta_json.ops] : [],
                },
            }),
        updated_at: new Date(),
    }

    // Cache the updated answer, which will handle updating quests
    return cacheAnswer(updatedAnswer, true, { preservePendingSyntheticUpvalues: false })
}

export function removeOptimisticAnswer(
    answerId: string | number,
    options: { questId?: string | number; childQuestIds?: Array<string | number> } = {},
) {
    if (!answerId) return

    const id = String(answerId)
    queryClient.removeQueries({ queryKey: ['answer', id], exact: true })
    entityCacheTouchedAt.answer.delete(id)
    invalidateNormalizedReadCachesForEntity('answer', id)

    options.childQuestIds
        ?.filter(Boolean)
        .map(childQuestId => String(childQuestId))
        .forEach(childQuestId => {
            const childQuest = getCachedQuest(childQuestId, new Set())
            if (
                !childQuest?.isLocalChildQuest &&
                !childQuest?.isLocalRootQuest &&
                !childQuest?.localChildQuest
            ) {
                return
            }

            queryClient.removeQueries({ queryKey: ['quest', childQuestId], exact: true })
            entityCacheTouchedAt.quest.delete(childQuestId)
            invalidateNormalizedReadCachesForEntity('quest', childQuestId)
        })

    if (!options.questId) return

    const questId = String(options.questId)
    queryClient.setQueryData(['quest', questId], (oldQuest: QuestWithAncestors | undefined) => {
        if (!oldQuest) return oldQuest

        return {
            ...oldQuest,
            sorted_answers: Array.isArray(oldQuest.sorted_answers)
                ? oldQuest.sorted_answers.filter(answer => String(answer?.id) !== id)
                : oldQuest.sorted_answers,
            sorted_answer_ids: Array.isArray(oldQuest.sorted_answer_ids)
                ? oldQuest.sorted_answer_ids.filter(sortedAnswerId => String(sortedAnswerId) !== id)
                : oldQuest.sorted_answer_ids,
            updated_at: new Date(),
        }
    })
    invalidateNormalizedReadCachesForEntity('quest', questId)
    touchQuestInStreamQueries(questId)
}

export function appendOptimisticReplyToQuest(quest: Quest, answer: Answer) {
    // A memoized title row or an open reply modal can retain a pre-hydration
    // snapshot. Append to the current thread, never replace it with that snapshot.
    const currentQuest = getCachedQuest(quest.id) || quest
    // Media preparation can outlast a newer socket update. Do not make this
    // append look stale to cacheQuest's answer-list freshness guard.
    const updatedAt =
        (parseCacheTimestamp(currentQuest.updated_at) ?? 0) >
        (parseCacheTimestamp(answer.updated_at) ?? 0)
            ? currentQuest.updated_at
            : answer.updated_at
    return cacheQuest(
        {
            ...currentQuest,
            updated_at: updatedAt,
            sorted_answer_ids: [...(currentQuest.sorted_answer_ids || []), answer.id],
            sorted_answers: [...(currentQuest.sorted_answers || []), answer],
        },
        { skipParent: true },
    )
}

export function updateCachedQuest(questId, newContent) {
    return cacheQuest({ ...getCachedQuest(questId, new Set()), ...newContent })
}
